import { createMemo, createSignal, For, onCleanup, onMount, Show, type Component } from "solid-js";
import { PanelLayout, type PanelStatus } from "@/shared/panel-base";
import { useToolClient } from "@/shared/tool-client";
import { useWs } from "@/shared/ws";
import { encryptCredentialPassword } from "@/shared/credential-encryption";
import type { AgentCredentialPublicKey, EncryptedCredential } from "@/shared/types";
import { isWsEnvelope } from "@/shared/ws-protocol";
import { CredentialInput, type CredentialInputResult } from "@/shared/credential-input";
import {
  sealCredentialVaultCredentialForAgent,
  requestCredentialVaultDirectRdpGrant,
} from "@/shared/credential-vault-client";
import { createCredentialGrantKey } from "@/shared/credential-grant";
import { registerPanel } from "@/panel-registry";
import "./watchguard.css";

type TabId = "rules" | "traffic" | "links" | "users" | "config" | "tcpdump" | "cache";

interface GatewayProbeResult {
  gateway: string | null;
  port: number;
  interfaceName: string | null;
  interfaceDescription: string | null;
  interfaceType: string | null;
  mac: string | null;
  reachable: boolean;
  latencyMs: number;
  error: string | null;
}

interface LoginResult {
  sessionId: string;
  gateway: string;
  username: string;
  role: string;
  prompt: string;
  connectedAt: string;
  portalAuth: PortalAuthResult | null;
  warning: string | null;
}

interface PortalAuthResult {
  attempted: boolean;
  success: boolean;
  gateway: string;
  port: number;
  username: string | null;
  domain: string | null;
  statusCode: number | null;
  location: string | null;
  cookies: number;
  error: string | null;
}

interface OverviewSection {
  id: string;
  title: string;
  command: string;
  output: string;
}

interface OverviewResult {
  gateway: string;
  role: string;
  sections: OverviewSection[];
}

interface LinkMonitorEntry {
  name: string;
  interfaceName: string | null;
  method: string | null;
  target: string | null;
  status: string | null;
  latency: string | null;
  packetLoss: string | null;
  jitter: string | null;
  raw: string;
}

interface LinkMonitorsResult {
  monitors: LinkMonitorEntry[];
  raw: string;
}

interface RuleSummary {
  name: string;
  normalizedName: string;
  enabled: boolean | null;
  action: string | null;
  sources: string[];
  destinations: string[];
  services: string[];
  nat: string | null;
  proxy: string | null;
  raw: string;
  snapshotId: number;
  updatedAt: string;
}

interface RulesListResult {
  rules: RuleSummary[];
  count: number;
  snapshotId: number;
  snapshotAgeMs: number;
}

interface RuleDetailResult {
  rule: RuleSummary | null;
  liveOutput: string | null;
  source: string;
  relatedTraffic: TrafficRow[];
}

interface ConfigRefreshResult {
  snapshotId: number;
  createdAt: string;
  hash: string;
  ruleCount: number;
  rules: RuleSummary[];
  rawBytes: number;
  redactedBytes: number;
}

interface ConfigTreeItem {
  id: string;
  label: string;
  kind: string;
  summary: string | null;
}

interface ConfigTreeSection {
  id: string;
  title: string;
  count: number;
  items: ConfigTreeItem[];
}

interface ConfigTreeResult {
  snapshotId: number;
  sections: ConfigTreeSection[];
}

interface TrafficRow {
  id: string;
  timeMs: number;
  time: string;
  action: string | null;
  policy: string | null;
  src: string | null;
  dst: string | null;
  srcPort: number | null;
  dstPort: number | null;
  proto: string | null;
  user: string | null;
  interfaceName: string | null;
  raw: string;
  logTag?: string | null;
  logType?: string | null;
  category?: string | null;
  severity?: string | null;
  msgId?: string | null;
  procId?: string | null;
  message?: string | null;
  srcIntf?: string | null;
  dstIntf?: string | null;
  srcIpNat?: string | null;
  srcPortNat?: number | null;
  routeType?: string | null;
  pcktLen?: number | null;
  duration?: number | null;
  sentBytes?: number | null;
  rcvdBytes?: number | null;
  sentPkts?: number | null;
  rcvdPkts?: number | null;
  ttl?: number | null;
  flags?: string | null;
  tcpInfo?: string | null;
  question?: string | null;
  recordType?: string | null;
  sni?: string | null;
  cn?: string | null;
  tlsVersion?: string | null;
  tlsProfile?: string | null;
  certIssuer?: string | null;
  certSubject?: string | null;
  proxyAct?: string | null;
  proxyAction?: string | null;
  op?: string | null;
  dstName?: string | null;
  arg?: string | null;
  categories?: string | null;
  appId?: number | null;
  appName?: string | null;
  appCatName?: string | null;
  appBehName?: string | null;
  sigVers?: string | null;
  ruleName?: string | null;
  srvIp?: string | null;
  srvPort?: number | null;
}

interface TrafficStartResult {
  jobId: string;
  watching: boolean;
  intervalMs: number;
}

interface TrafficQueryResult {
  rows: TrafficRow[];
  count: number;
}

interface TrafficTopEntry {
  label: string;
  count: number;
}

interface TrafficStatsResult {
  total: number;
  filter: number;
  proxy: number;
  status: number;
  allow: number;
  deny: number;
  byProcId: Record<string, number>;
  byMsgId: Record<string, number>;
  topSrc: TrafficTopEntry[];
  topDst: TrafficTopEntry[];
  topPolicy: TrafficTopEntry[];
  topApp: TrafficTopEntry[];
  earliestMs: number | null;
  latestMs: number | null;
}

interface TrafficDeltaPayload {
  jobId?: string;
  seq?: number;
  rows?: TrafficRow[];
  inserted?: number;
  lastSeenHash?: string | null;
}

interface AuthenticatedUserEntry {
  user: string;
  ip: string | null;
  authType: string | null;
  groups: string[];
  source: string | null;
  session: string | null;
  raw: string;
}

interface UsersResult {
  authenticatedUsers: AuthenticatedUserEntry[];
  who: string;
  users: string;
  groups: string;
  authUserGroups: string;
  l2tpAuthUsers: string;
  l2tpSessions: string;
  vpnStatus: string;
  authLog: string;
}

interface TcpdumpStartResult {
  jobId: string;
  async: boolean;
  command: string;
}

interface TcpdumpStatusResult {
  running: boolean;
  jobId: string | null;
  command: string | null;
  startedAt: string | null;
}

interface CacheStatsResult {
  dbPath: string;
  snapshots: number;
  rules: number;
  traffic: number;
  tcpdump: number;
  trafficCap: number;
  tcpdumpCap: number;
}

// --- Modèles Policy Manager (Fireware moderne, vue "From-To") ---

type PmTabId = "policies" | "aliases" | "services" | "nat" | "schedules";

interface FirewallPolicy {
  order: number;
  name: string;
  leafName: string | null;
  enabled: boolean;
  action: string; // Allow | Block | Proxy
  type: string | null;
  service: string | null;
  description: string | null;
  from: string[];
  to: string[];
  fromResolved: string[];
  toResolved: string[];
  nat: string | null;
  applyOneToOneNat: boolean;
  applyDnatGlobal: boolean;
  proxy: string | null;
  schedule: string | null;
  log: boolean;
  logForReport: boolean | null;
  ips: boolean;
  quota: boolean | null;
  geoAction: string | null;
  geoDenyPage: boolean | null;
  torBlock: boolean | null;
  connectionRate: number | null;
  sendTcpReset: boolean | null;
  autoBlock: boolean | null;
  notification: boolean | null;
  snmp: boolean | null;
  idleTimeout: number | null;
  qosPriority: string | null;
  policyRouting: string | null;
  tags: string[];
  appAction: string | null;
  sdwanAction: string | null;
  tlsSetting: string | null;
  rawXml: string;
}

interface FirewallPoliciesResult {
  policies: FirewallPolicy[];
  count: number;
  snapshotId: number;
  snapshotAgeMs: number;
  view: string | null;
}

interface ResolveTreeNode {
  name: string;
  kind: string;
  display: string;
  children: ResolveTreeNode[];
}

interface AliasMemberInfo {
  type: string;
  address: string | null;
  interface: string | null;
  user: string | null;
  aliasRef: string | null;
}

interface AliasEntry {
  name: string;
  description: string | null;
  members: AliasMemberInfo[];
  resolved: string[];
}

interface AliasesListResult {
  aliases: AliasEntry[];
  count: number;
  snapshotId: number;
}

interface AddressMemberInfo {
  kind: string;
  value: string;
  extra: string | null;
}

interface AddressGroupEntry {
  name: string;
  description: string | null;
  members: AddressMemberInfo[];
}

interface AddressGroupsListResult {
  groups: AddressGroupEntry[];
  count: number;
  snapshotId: number;
}

interface ServiceItemInfo {
  protocol: string;
  port: number;
}

interface ServiceEntry {
  name: string;
  description: string | null;
  proxyType: string | null;
  items: ServiceItemInfo[];
}

interface ServicesListResult {
  services: ServiceEntry[];
  count: number;
  snapshotId: number;
}

interface NatItemInfo {
  addrType: string;
  port: number;
  extAddrName: string | null;
  interface: string | null;
  addrName: string | null;
  addrResolved: string[];
}

interface NatRuleEntry {
  name: string;
  description: string | null;
  type: string;
  items: NatItemInfo[];
}

interface DnatEntry {
  fromAlias: string;
  toAlias: string;
}

interface NatRulesListResult {
  natRules: NatRuleEntry[];
  dnat: DnatEntry[];
  count: number;
  snapshotId: number;
}

interface ScheduleEntry {
  name: string;
  description: string | null;
  summary: string;
}

interface SchedulesListResult {
  schedules: ScheduleEntry[];
  count: number;
  snapshotId: number;
}

interface ProxyActionSummary {
  name: string;
  description: string | null;
  type: string;
  typeAttr: string | null;
  ipsEnabled: boolean;
  ipsSpyware: boolean;
  ipsProtectionType: string | null;
  webBlockerEnabled: boolean;
  trustedHostsCount: number;
  requestTimeout: number | null;
  websocketEnabled: boolean | null;
  reverseProxy: ReverseProxySummary | null;
}

interface ReverseProxySummary {
  redirectTo: string | null;
  tlsProfile: string | null;
  tlsMinClient: string | null;
  tlsMinServer: string | null;
  timeout: number | null;
  domainRules: ReverseProxyDomainRule[];
}

interface ReverseProxyDomainRule {
  name: string;
  matchType: string;
  pattern: string | null;
  inspect: string | null;
  ipv4Dest: string | null;
  serverCert: string | null;
  log: boolean;
  fallthrough: boolean;
}

interface PolicyDetailResult {
  policy: FirewallPolicy;
  serviceEntry: ServiceEntry | null;
  natRule: NatRuleEntry | null;
  proxyAction: ProxyActionSummary | null;
  scheduleEntry: ScheduleEntry | null;
  fromTree: ResolveTreeNode[];
  toTree: ResolveTreeNode[];
  relatedTraffic: TrafficRow[];
  liveOutput: string | null;
  source: string;
}

interface FirewareConfigCounts {
  policies: number;
  aliases: number;
  addressGroups: number;
  services: number;
  natRules: number;
  dnatEntries: number;
  schedules: number;
  proxyActions: number;
}

interface GeoActionEntry {
  name: string;
  description: string | null;
}

interface GeoBlockingSummary {
  enabled: boolean;
  actions: GeoActionEntry[];
}

interface FirewareConfigSummary {
  forVersion: string | null;
  rsVersion: string | null;
  view: string | null;
  compatVersion: string | null;
  computedAt: string;
  counts: FirewareConfigCounts;
  geolocation: GeoBlockingSummary;
}

const CREDENTIAL_KEY_REQUEST_TIMEOUT_MS = 12_000;
const MAX_TRAFFIC_ROWS = 1_000;

function fmtAge(ms: number): string {
  if (ms < 0) return "aucun snapshot";
  if (ms < 60_000) return `${Math.max(1, Math.round(ms / 1000))} s`;
  if (ms < 3_600_000) return `${Math.round(ms / 60_000)} min`;
  return `${Math.round(ms / 3_600_000)} h`;
}

function fmtTime(value: string | null | undefined): string {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

function joinShort(values: string[], empty = "any"): string {
  if (!values.length) return empty;
  if (values.length <= 3) return values.join(", ");
  return `${values.slice(0, 3).join(", ")} +${values.length - 3}`;
}

function actionTone(action: string | null | undefined): string {
  const v = (action ?? "").toLowerCase();
  if (v.includes("deny") || v.includes("drop") || v.includes("block")) return "deny";
  if (v.includes("allow") || v.includes("permit")) return "allow";
  if (v.includes("proxy")) return "proxy";
  return "neutral";
}

// Icône + libellé court d'action, comme la colonne Action du Policy Manager.
function pmActionGlyph(action: string | null | undefined): { tone: string; icon: string; label: string } {
  const tone = actionTone(action);
  if (tone === "deny") return { tone, icon: "✕", label: "Block" };
  if (tone === "proxy") return { tone, icon: "⚙", label: "Proxy" };
  if (tone === "allow") return { tone, icon: "✓", label: "Allow" };
  return { tone, icon: "?", label: action ?? "—" };
}

function linkTone(status: string | null | undefined): string {
  const v = (status ?? "").toLowerCase();
  if (v.includes("up") || v.includes("ok") || v.includes("success") || v.includes("reachable")) return "up";
  if (v.includes("down") || v.includes("fail") || v.includes("error") || v.includes("unreachable")) return "down";
  return "unknown";
}

// Libellé lisible d'une catégorie de log Fireware (filter / proxy / status).
function trafficCategoryLabel(cat: string | null | undefined): string {
  const c = (cat ?? "").toLowerCase();
  if (c === "filter") return "Filtrage";
  if (c === "proxy") return "Proxy";
  if (c === "status") return "Système";
  return "Autre";
}

// Icône courte pour une catégorie de log.
function trafficCategoryIcon(cat: string | null | undefined): string {
  const c = (cat ?? "").toLowerCase();
  if (c === "filter") return "🛡";
  if (c === "proxy") return "⚙";
  if (c === "status") return "ℹ";
  return "?";
}

// Formate un volume d'octets de façon compacte (1536 -> "1.5 Ko").
function fmtBytes(n: number | null | undefined): string {
  if (n == null || n === 0) return "";
  const units = ["o", "Ko", "Mo", "Go"];
  let v = n;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v >= 100 ? Math.round(v) : v.toFixed(1).replace(/\.0$/, "")} ${units[i]}`;
}

// Rendu compact d'un hôte (IP + port optionnel) pour les cellules du tableau.
function fmtHost(ip: string | null | undefined, port: number | null | undefined): string {
  if (!ip) return "";
  return port ? `${ip}:${port}` : ip;
}

// Construit un libellé de détail court selon la nature du log (DNS question, app identifiée,
// WebBlocker, message Fireware). Permet d'avoir une colonne "Détail" utile sans ouvrir le drawer.
function trafficRowDetail(r: TrafficRow): string {
  if (r.question) return `DNS ${r.question}`;
  if (r.appName) return r.appName;
  if (r.categories) return r.categories;
  if (r.sni || r.dstName) return r.sni ?? r.dstName ?? "";
  if (r.proxyAction) return r.proxyAction;
  if (r.op) return `${r.op} ${r.dstName ?? ""}`.trim();
  if (r.message) return r.message.length > 80 ? `${r.message.slice(0, 77)}…` : r.message;
  return "";
}

// Icône + libellé court du service (basé sur le port de destination et le protocole normalisé).
// Retourne un objet {icon, label} : 📨 DNS, 🔒 HTTPS, 🌐 HTTP, 📧 SMTP, 📁 FTP, 🔁 générique TCP/UDP.
interface ServiceGlyph {
  icon: string;
  label: string;
}

function serviceGlyph(proto: string | null | undefined, dstPort: number | null | undefined): ServiceGlyph {
  const p = (proto ?? "").toLowerCase();
  if (dstPort === 53 || p.includes("dns")) return { icon: "📨", label: "DNS" };
  if (dstPort === 443 || p.includes("https")) return { icon: "🔒", label: "HTTPS" };
  if (dstPort === 80 || p.includes("http")) return { icon: "🌐", label: "HTTP" };
  if (dstPort === 25 || dstPort === 465 || dstPort === 587) return { icon: "📧", label: "SMTP" };
  if (dstPort === 110 || dstPort === 995) return { icon: "📥", label: "POP3" };
  if (dstPort === 143 || dstPort === 993) return { icon: "📥", label: "IMAP" };
  if (dstPort === 21 || p.includes("ftp")) return { icon: "📁", label: "FTP" };
  if (p === "icmp") return { icon: "💠", label: "ICMP" };
  if (dstPort != null) return { icon: "🔁", label: `${dstPort}/${p || "tcp"}` };
  if (p) return { icon: "🔁", label: p };
  return { icon: "—", label: "" };
}

// Symbole d'action : ✓ allow, ✕ deny, 🔄 proxy, ⊘ drop/block. Légèrement distinct de actionTone
// car on veut un glyphe visible dans la colonne Action.
function actionSymbol(action: string | null | undefined, category: string | null | undefined): string {
  const a = (action ?? "").toLowerCase();
  if (a.includes("deny") || a.includes("drop") || a.includes("block") || a.includes("reject")) return "✕";
  if ((category ?? "") === "proxy") return "🔄";
  if (a.includes("allow") || a.includes("permit")) return "✓";
  return "•";
}

interface DetectionBadge {
  symbol: string;
  label: string;
  tone: string;
}

// Calcule la rangée de micro-symboles de détection présents sur une ligne de log. Chaque symbole
// porte un libellé de tooltip. On regroupe ici tout ce qui serait sinon des colonnes vides :
// SD-WAN, TLS, App Control, WebBlocker, DNS, NAT, priorité élevée, volume.
function detectionBadges(r: TrafficRow): DetectionBadge[] {
  const badges: DetectionBadge[] = [];
  if (r.routeType) badges.push({ symbol: "🌐", label: `Routage ${r.routeType}`, tone: "sdwan" });
  if (r.tlsVersion || r.tlsProfile)
    badges.push({ symbol: "🔒", label: `TLS ${r.tlsVersion ?? ""}`.trim(), tone: "tls" });
  if (r.appName) badges.push({ symbol: "📋", label: `App Control : ${r.appName}`, tone: "app" });
  if (r.categories) badges.push({ symbol: "🚫", label: `WebBlocker : ${r.categories}`, tone: "blocker" });
  if (r.question) badges.push({ symbol: "📨", label: `DNS ${r.question} (${r.recordType ?? "?"})`, tone: "dns" });
  if (r.srcIpNat) badges.push({ symbol: "↔", label: `NAT source ${r.srcIpNat}`, tone: "nat" });
  const sev = (r.severity ?? "").toLowerCase();
  if (sev && !["info", "debug", "notice"].includes(sev)) {
    badges.push({ symbol: "⚠", label: `Priorité ${r.severity}`, tone: "warn" });
  }
  if (r.sentBytes != null && r.sentBytes > 0 && r.rcvdBytes != null && r.rcvdBytes > 0) {
    badges.push({ symbol: "📊", label: `Volume ↑${fmtBytes(r.sentBytes)} ↓${fmtBytes(r.rcvdBytes)}`, tone: "vol" });
  }
  return badges;
}

// Résumé en langage naturel d'une ligne de log, adapté au type. C'est la "phrase d'accroche"
// affichée en haut de la modale : elle doit donner en un coup d'œil l'essentiel de l'événement,
// avec un vocabulaire différent selon qu'il s'agit d'un allow/deny firewall, d'une inspection
// proxy, d'une décision WebBlocker ou d'un événement système (link monitor).
interface HeroLine {
  text: string;
  tone: string;
}

function trafficHero(r: TrafficRow): HeroLine {
  const cat = (r.category ?? "").toLowerCase();
  const act = (r.action ?? "").toLowerCase();
  const dst = fmtHost(r.dst, r.dstPort);
  const src = fmtHost(r.src, r.srcPort);
  const host = r.sni ?? r.dstName;

  // --- Événement système (link monitor, admd…) : on met en avant le message. ---
  if (cat === "status") {
    const msg = r.message ?? r.logTag ?? "Événement système";
    // On essaie d'extraire la cible du link monitor ("... from Ping target X.X.X.X").
    const m = msg.match(/target\s+([\d.]+)/i);
    if (m) return { text: `${r.procId ?? "Link Monitor"} : pas de réponse de ${m[1]}`, tone: "warn" };
    return { text: msg.length > 120 ? `${msg.slice(0, 117)}…` : msg, tone: act.includes("den") ? "deny" : "warn" };
  }

  // --- Décision WebBlocker (ProxyMatch avec action="WebBlocker.*" ou catégories). ---
  if (cat === "proxy" && (r.categories || (r.proxyAction ?? "").toLowerCase().includes("webblocker"))) {
    const verbe = act.includes("den") ? "Bloqué" : "Autorisé";
    const cat2 = r.categories ?? r.proxyAction ?? "catégorie inconnue";
    return {
      text: `${verbe} par WebBlocker : ${cat2}${host ? ` (${host})` : ""}`,
      tone: act.includes("den") ? "deny" : "allow",
    };
  }

  // --- Inspection proxy HTTPS/HTTP : on met en avant l'hôte + TLS. ---
  if (cat === "proxy") {
    const verbe = act.includes("den") ? "Bloqué" : "Inspecté";
    const tls = r.tlsVersion ? ` · ${r.tlsVersion}` : "";
    const app = r.appName ? ` · ${r.appName}` : "";
    return {
      text: `${verbe} par proxy ${host ? host : dst}${tls}${app}`,
      tone: act.includes("den") ? "deny" : "proxy",
    };
  }

  // --- Filtrage firewall : allow / deny sur le 5-tuple. ---
  const verbe = act.includes("den") ? "Bloqué" : "Autorisé";
  const svc = serviceGlyph(r.proto, r.dstPort).label;
  const dnsNote = r.question ? ` — ${r.question}` : "";
  const natNote = r.srcIpNat ? ` (NAT ${r.srcIpNat})` : "";
  const route = r.routeType === "SD-WAN" ? " · SD-WAN" : "";
  const target = host ?? dst;
  return {
    text: `${verbe} ${svc ? `(${svc}) ` : ""}${src || "?"} → ${target || "?"}${dnsNote}${natNote}${route}`,
    tone: act.includes("den") ? "deny" : "allow",
  };
}

function uniqueById(rows: TrafficRow[]): TrafficRow[] {
  const seen = new Set<string>();
  const next: TrafficRow[] = [];
  for (const row of rows) {
    if (seen.has(row.id)) continue;
    seen.add(row.id);
    next.push(row);
  }
  return next;
}

const [wgProbe, setWgProbe] = createSignal<GatewayProbeResult | null>(null);
const [wgSession, setWgSession] = createSignal<LoginResult | null>(null);
const [wgRules, setWgRules] = createSignal<RuleSummary[]>([]);
const [wgRulesMeta, setWgRulesMeta] = createSignal<{ snapshotId: number; snapshotAgeMs: number; count: number } | null>(
  null,
);
const [wgSelectedRule, setWgSelectedRule] = createSignal<RuleDetailResult | null>(null);
const [wgSelectedRuleName, setWgSelectedRuleName] = createSignal<string | null>(null);
const [wgTrafficRows, setWgTrafficRows] = createSignal<TrafficRow[]>([]);
const [wgTrafficJobId, setWgTrafficJobId] = createSignal<string | null>(null);
const [wgTrafficStats, setWgTrafficStats] = createSignal<TrafficStatsResult | null>(null);
const [wgTrafficCategory, setWgTrafficCategory] = createSignal<"all" | "filter" | "proxy" | "status">("all");
const [wgSelectedTraffic, setWgSelectedTraffic] = createSignal<TrafficRow | null>(null);
const [wgOverview, setWgOverview] = createSignal<OverviewResult | null>(null);
const [wgLinkMonitors, setWgLinkMonitors] = createSignal<LinkMonitorsResult | null>(null);
const [wgUsers, setWgUsers] = createSignal<UsersResult | null>(null);
const [wgConfigTree, setWgConfigTree] = createSignal<ConfigTreeResult | null>(null);
const [wgLastRefresh, setWgLastRefresh] = createSignal<ConfigRefreshResult | null>(null);
const [wgTcpdumpCommand, setWgTcpdumpCommand] = createSignal<string | null>(null);
const [wgTcpdumpLines, setWgTcpdumpLines] = createSignal<string[]>([]);
const [wgTcpdumpJobId, setWgTcpdumpJobId] = createSignal<string | null>(null);
const [wgTcpdumpRunning, setWgTcpdumpRunning] = createSignal(false);
const [wgCacheStats, setWgCacheStats] = createSignal<CacheStatsResult | null>(null);

// --- État Policy Manager (vue "From-To" fidèle au WatchGuard Policy Manager) ---
const [wgPolicies, setWgPolicies] = createSignal<FirewallPolicy[]>([]);
const [wgPoliciesMeta, setWgPoliciesMeta] = createSignal<{
  snapshotId: number;
  snapshotAgeMs: number;
  view: string | null;
} | null>(null);
const [wgPolicyDetail, setWgPolicyDetail] = createSignal<PolicyDetailResult | null>(null);
const [wgPolicyKey, setWgPolicyKey] = createSignal<string | null>(null);
const [wgPmTab, setWgPmTab] = createSignal<PmTabId>("policies");
const [wgPolicyQuery, setWgPolicyQuery] = createSignal("");
const [wgAliases, setWgAliases] = createSignal<AliasEntry[]>([]);
const [wgAddressGroups, setWgAddressGroups] = createSignal<AddressGroupEntry[]>([]);
const [wgServices, setWgServices] = createSignal<ServiceEntry[]>([]);
const [wgNatRules, setWgNatRules] = createSignal<NatRulesListResult | null>(null);
const [wgSchedules, setWgSchedules] = createSignal<ScheduleEntry[]>([]);
const [wgConfigSummary, setWgConfigSummary] = createSignal<FirewareConfigSummary | null>(null);

let wgOffTraffic: (() => void) | null = null;
let wgOffTcpdump: (() => void) | null = null;

// Arbre de résolution d'alias récursif et expandable (alias -> sous-alias -> IP),
// reproduit dans le drawer Members du Policy Manager.
const ResolveTreeView: Component<{ nodes: ResolveTreeNode[]; level?: number }> = (props) => {
  return (
    <ul class="wg-resolve-tree" style={{ "padding-left": `${(props.level ?? 0) * 14}px` }}>
      <For each={props.nodes}>
        {(node) => {
          const leaf = node.children.length === 0;
          const [open, setOpen] = createSignal(!leaf && (props.level ?? 0) < 1);
          return (
            <li class="wg-resolve-node" data-kind={node.kind}>
              <Show
                when={leaf}
                fallback={
                  <button type="button" class="wg-resolve-toggle" onClick={() => setOpen((o) => !o)}>
                    <span>{open() ? "▾" : "▸"}</span>
                    <span class="wg-resolve-label">{node.display}</span>
                  </button>
                }
              >
                <span class="wg-resolve-leaf">{node.display}</span>
              </Show>
              <Show when={!leaf && open()}>
                <ResolveTreeView nodes={node.children} level={(props.level ?? 0) + 1} />
              </Show>
            </li>
          );
        }}
      </For>
    </ul>
  );
};

interface WatchGuardPanelProps {
  initialTab?: TabId;
  lockedTab?: boolean;
  title?: string;
  // Préférence credential VOLT par usage sur ce host : { "watchguard-cli":
  // uuid, "watchguard-portal": uuid }. Pré-remplit les sélecteurs VOLT à
  // l'ouverture du panneau ; l'opérateur peut toujours override.
  vaultDefaults?: Record<string, string>;
}

const WatchGuardPanel: Component<WatchGuardPanelProps> = (props) => {
  const client = useToolClient();
  const ws = useWs();

  const [activeTab, setActiveTab] = createSignal<TabId>(props.initialTab ?? "rules");
  const [status, setStatus] = createSignal<PanelStatus | undefined>();
  const [busy, setBusy] = createSignal(false);
  const probe = wgProbe;
  const setProbe = setWgProbe;
  const session = wgSession;
  const setSession = setWgSession;
  const [portalEnabled, setPortalEnabled] = createSignal(false);
  const [portalPort, setPortalPort] = createSignal("4100");
  // ── Résultats des composants <CredentialInput> (CLI + portail) ──
  // Ces signaux sont alimentés par les callbacks onResult des composants
  // d'identification. Le login() les consomme pour obtenir le credential
  // scellé (VOLT) ou le mot de passe clair (manuel).
  const [cliResult, setCliResult] = createSignal<CredentialInputResult>({ mode: "manual", ready: false });
  const [portalResult, setPortalResult] = createSignal<CredentialInputResult>({
    mode: "manual",
    ready: false,
  });
  const rules = wgRules;
  const setRules = setWgRules;
  const rulesMeta = wgRulesMeta;
  const setRulesMeta = setWgRulesMeta;
  const [ruleQuery] = createSignal("");
  const selectedRule = wgSelectedRule;
  const setSelectedRule = setWgSelectedRule;
  const selectedRuleName = wgSelectedRuleName;
  const setSelectedRuleName = setWgSelectedRuleName;
  const trafficRows = wgTrafficRows;
  const setTrafficRows = setWgTrafficRows;
  const [trafficQuery, setTrafficQuery] = createSignal("");
  const trafficJobId = wgTrafficJobId;
  const setTrafficJobId = setWgTrafficJobId;
  const trafficStats = wgTrafficStats;
  const setTrafficStats = setWgTrafficStats;
  const trafficCategory = wgTrafficCategory;
  const setTrafficCategory = setWgTrafficCategory;
  const selectedTraffic = wgSelectedTraffic;
  const setSelectedTraffic = setWgSelectedTraffic;
  const overview = wgOverview;
  const setOverview = setWgOverview;
  const linkMonitors = wgLinkMonitors;
  const setLinkMonitors = setWgLinkMonitors;
  const users = wgUsers;
  const setUsers = setWgUsers;
  const configTree = wgConfigTree;
  const setConfigTree = setWgConfigTree;
  const lastRefresh = wgLastRefresh;
  const setLastRefresh = setWgLastRefresh;
  const [tcpdumpPreset, setTcpdumpPreset] = createSignal("dns");
  const [tcpdumpTarget, setTcpdumpTarget] = createSignal("");
  const [tcpdumpPort, setTcpdumpPort] = createSignal("443");
  const tcpdumpCommand = wgTcpdumpCommand;
  const setTcpdumpCommand = setWgTcpdumpCommand;
  const tcpdumpLines = wgTcpdumpLines;
  const setTcpdumpLines = setWgTcpdumpLines;
  const tcpdumpJobId = wgTcpdumpJobId;
  const setTcpdumpJobId = setWgTcpdumpJobId;
  const tcpdumpRunning = wgTcpdumpRunning;
  const setTcpdumpRunning = setWgTcpdumpRunning;
  const cacheStats = wgCacheStats;
  const setCacheStats = setWgCacheStats;

  const connected = () => !!session();
  const canUseTools = () => ws.state.nativeAgentOnline && ws.state.supportApproved;
  // Les policies sont déjà en ordre d'évaluation (tri natif du Policy Manager via order_idx).
  // On applique juste le filtre textuel sans réordonner.
  const filteredPolicies = createMemo(() => {
    const q = wgPolicyQuery().trim().toLowerCase();
    if (!q) return wgPolicies();
    return wgPolicies().filter((p) =>
      [
        String(p.order),
        p.name,
        p.leafName ?? "",
        p.action,
        p.type ?? "",
        p.service ?? "",
        p.description ?? "",
        p.nat ?? "",
        p.proxy ?? "",
        p.schedule ?? "",
        ...p.from,
        ...p.to,
        ...p.fromResolved,
        ...p.toResolved,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  });
  const filteredTraffic = createMemo(() => {
    const q = trafficQuery().trim().toLowerCase();
    const cat = trafficCategory();
    return trafficRows().filter((r) => {
      if (cat !== "all" && (r.category ?? "filter") !== cat) return false;
      if (!q) return true;
      return [
        r.action,
        r.policy,
        r.src,
        r.dst,
        r.proto,
        r.user,
        r.interfaceName,
        r.message,
        r.sni,
        r.dstName,
        r.appName,
        r.categories,
        r.logTag,
        r.procId,
        r.question,
        r.raw,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q);
    });
  });
  const linkKpis = createMemo(() => {
    const rows = linkMonitors()?.monitors ?? [];
    let up = 0;
    let down = 0;
    for (const row of rows) {
      const tone = linkTone(row.status);
      if (tone === "up") up += 1;
      if (tone === "down") down += 1;
    }
    return { total: rows.length, up, down };
  });
  const authUsers = createMemo(() => users()?.authenticatedUsers ?? []);

  async function requestCredentialKeyOnce(): Promise<AgentCredentialPublicKey> {
    if (!ws.isOpen()) throw new Error("Connexion WS indisponible.");
    const requestId = crypto.randomUUID();
    return new Promise((resolve, reject) => {
      let off = () => {};
      const timer = window.setTimeout(() => {
        off();
        reject(new Error("Clé publique agent non reçue."));
      }, CREDENTIAL_KEY_REQUEST_TIMEOUT_MS);
      off = ws.on(
        (m) => {
          const msg = m as { type?: string; requestId?: string };
          return (
            (msg.type === "agent:credential_key" || msg.type === "agent:credential_key_error") &&
            (!msg.requestId || msg.requestId === requestId)
          );
        },
        (m) => {
          window.clearTimeout(timer);
          off();
          const msg = m as { type?: string; message?: string };
          if (msg.type === "agent:credential_key_error") {
            reject(new Error(msg.message || "Clé publique agent refusée par le relais."));
            return;
          }
          resolve(m as AgentCredentialPublicKey);
        },
      );
      ws.send({ type: "agent:credential_key_request", requestId } as Parameters<typeof ws.send>[0]);
    });
  }

  async function encryptSecret(value: string): Promise<EncryptedCredential> {
    if (!ws.state.supportApproved) throw new Error("Consentement de session requis.");
    const key = ws.state.credentialKey ?? (await requestCredentialKeyOnce());
    return encryptCredentialPassword(key, value);
  }

  // Scelle un credential VOLT vers l'agent au moment du login (fallback si le
  // composant <CredentialInput> n'a pas encore terminé son scellage async).
  // Importé ici car le panneau a accès au contexte WS complet.
  async function sealVaultForAgent(credentialId: string): Promise<EncryptedCredential> {
    if (!ws.state.supportApproved) throw new Error("Consentement de session requis.");
    const agentPublicKey = ws.state.credentialKey ?? (await requestCredentialKeyOnce());
    const sealed = await sealCredentialVaultCredentialForAgent(credentialId, {
      agentPublicKey,
      purpose: "watchguard-login",
      ttlSeconds: 60,
    });
    const cred =
      (sealed as { sealed?: EncryptedCredential; credential?: EncryptedCredential }).sealed ??
      (sealed as { credential?: EncryptedCredential }).credential;
    if (!cred) throw new Error("Le scellement du credential VOLT n'a pas retourné de secret.");
    return cred;
  }

  async function callTool<R>(tool: string, params: Record<string, unknown> = {}, timeoutMs = 60_000): Promise<R> {
    return client.call<R>(tool, params, { timeoutMs });
  }

  async function refreshProbe() {
    try {
      const result = await callTool<GatewayProbeResult>("watchguard_probe", {}, 10_000);
      setProbe(result);
      if (result.gateway)
        setStatus({ text: `Passerelle ${result.gateway}:${result.port}`, level: result.reachable ? "info" : "warn" });
    } catch (err) {
      setStatus({ text: err instanceof Error ? err.message : String(err), level: "danger" });
    }
  }

  async function login() {
    if (!canUseTools()) {
      setStatus({ text: "Agent natif ou consentement indisponible.", level: "warn" });
      return;
    }
    const cli = cliResult();
    // En mode VOLT, on accepte même si ready:false (le scellage async du
    // composant peut ne pas être terminé) : on rescelle ici synchronement.
    const cliOk = cli.ready || (cli.mode === "volt" && cli.credentialId);
    if (!cliOk) {
      setStatus({ text: "Identifiant CLI WatchGuard requis (ou sélectionner un credential VOLT).", level: "warn" });
      return;
    }
    if (portalEnabled()) {
      const portal = portalResult();
      const portalOk = portal.ready || (portal.mode === "volt" && portal.credentialId);
      if (!portalOk) {
        setStatus({
          text: "Identifiants portail 4100 requis (ou sélectionner un credential VOLT portail).",
          level: "warn",
        });
        return;
      }
    }
    setBusy(true);
    setStatus({
      text: portalEnabled() ? "Pré-auth 4100 puis connexion Fireware CLI…" : "Connexion Fireware CLI…",
      level: "info",
    });
    try {
      // Credential CLI : VOLT (scellé par composant ou rescellé ici) ou
      // mot de passe manuel (chiffré vers l'agent).
      let credential: EncryptedCredential | undefined;
      // passwordFallback : mot de passe en clair pour les agents qui ne
      // supportent pas PrepareWatchGuardCredentialParams (AOT < fix). Le
      // password voyage chiffré TLS via WSS. Quand l'agent supporte le
      // déchiffrement credential, il ignore ce fallback.
      let passwordFallback = "";
      let cliUser = cli.username || "status";
      if (cli.mode === "volt") {
        // VOLT : on a besoin du mot de passe en clair pour le fallback.
        // On le récupère via le grant opérateur (direct-rdp-grant) qui
        // déchiffre côté browser.
        try {
          const grantKey = await createCredentialGrantKey();
          const grant = await requestCredentialVaultDirectRdpGrant(cli.credentialId!, {
            publicKey: grantKey.publicKey,
          });
          passwordFallback = await grantKey.decryptCredential(grant.credential!);
          if (grant.username && !cliUser) cliUser = grant.username;
        } catch {
          // Fallback échoué : on envoie quand même le credential scellé.
        }
        credential = cli.sealed ?? (cli.credentialId ? await sealVaultForAgent(cli.credentialId) : undefined);
      } else {
        passwordFallback = cli.password || "";
        credential = await encryptSecret(cli.password || "");
      }
      // Credential portail 4100 : VOLT (scellé) ou mot de passe manuel.
      let portalCredential: EncryptedCredential | undefined;
      let portalPasswordFallback = "";
      let portalUser = "";
      let portalDom = "Firebox-DB";
      if (portalEnabled()) {
        const portal = portalResult();
        if (portal.mode === "volt") {
          try {
            const grantKey = await createCredentialGrantKey();
            const grant = await requestCredentialVaultDirectRdpGrant(portal.credentialId!, {
              publicKey: grantKey.publicKey,
            });
            portalPasswordFallback = await grantKey.decryptCredential(grant.credential!);
            if (grant.username) portalUser = grant.username;
            if (grant.domain) portalDom = grant.domain;
          } catch {
            // ignore
          }
          portalCredential =
            portal.sealed ?? (portal.credentialId ? await sealVaultForAgent(portal.credentialId) : undefined);
        } else {
          portalPasswordFallback = portal.password || "";
          portalCredential = await encryptSecret(portal.password || "");
          if (portal.username) portalUser = portal.username;
        }
      }
      const result = await callTool<LoginResult>(
        "watchguard_login",
        {
          gateway: probe()?.gateway ?? undefined,
          username: cliUser,
          // credential (chiffré agent) pour les versions futures ; password
          // (clair, protégé TLS via WSS) pour les agents actuels qui ne
          // supportent pas PrepareWatchGuardCredentialParams en AOT.
          credential,
          password: passwordFallback || undefined,
          portalAuth: portalEnabled(),
          portalUsername: portalEnabled() ? portalUser.trim() : undefined,
          portalDomain: portalEnabled() ? portalDom : undefined,
          portalPort: portalEnabled() ? Number.parseInt(portalPort(), 10) || 4100 : undefined,
          portalCredential,
          portalPassword: portalEnabled() ? portalPasswordFallback || undefined : undefined,
        },
        60_000,
      );
      setSession(result);
      setStatus({
        text:
          result.warning ??
          `${result.portalAuth?.success ? "Portail 4100 OK · " : ""}Connecté ${result.gateway} (${result.role})`,
        level: result.warning ? "warn" : "info",
      });
      await Promise.all([
        refreshOverview(),
        refreshLinks(),
        refreshUsers(),
        refreshRules(),
        refreshTraffic(),
        refreshCache(),
      ]);
    } catch (err) {
      setStatus({ text: err instanceof Error ? err.message : String(err), level: "danger" });
    } finally {
      setBusy(false);
    }
  }

  async function logout() {
    const s = session();
    if (!s) return;
    stopTraffic();
    setBusy(true);
    try {
      await callTool("watchguard_logout", { sessionId: s.sessionId }, 10_000);
      setSession(null);
      setStatus({ text: "Session WatchGuard fermée.", level: "info" });
    } catch (err) {
      setStatus({ text: err instanceof Error ? err.message : String(err), level: "warn" });
    } finally {
      setBusy(false);
    }
  }

  async function refreshOverview() {
    const s = session();
    if (!s) return;
    const result = await callTool<OverviewResult>("watchguard_overview", { sessionId: s.sessionId }, 45_000);
    setOverview(result);
  }

  async function refreshLinks() {
    const s = session();
    if (!s) return;
    const result = await callTool<LinkMonitorsResult>("watchguard_link_monitors", { sessionId: s.sessionId }, 30_000);
    setLinkMonitors(result);
  }

  async function refreshUsers() {
    const s = session();
    if (!s) return;
    const result = await callTool<UsersResult>("watchguard_users", { sessionId: s.sessionId }, 70_000);
    setUsers(result);
  }

  async function refreshRules() {
    const result = await callTool<RulesListResult>(
      "watchguard_rules_list",
      { query: ruleQuery(), limit: 1_500 },
      30_000,
    );
    setRules(result.rules);
    setRulesMeta({ snapshotId: result.snapshotId, snapshotAgeMs: result.snapshotAgeMs, count: result.count });
  }

  async function refreshConfig() {
    const s = session();
    if (!s) return;
    setBusy(true);
    setStatus({ text: "Export configuration WatchGuard en cours…", level: "info" });
    try {
      const result = await callTool<ConfigRefreshResult>(
        "watchguard_config_refresh",
        { sessionId: s.sessionId },
        90_000,
      );
      setLastRefresh(result);
      setRules(result.rules);
      setRulesMeta({ snapshotId: result.snapshotId, snapshotAgeMs: 0, count: result.ruleCount });
      await refreshConfigTree();
      // Recharge la vue Policy Manager (ordre d'évaluation + objets réseau).
      await Promise.all([
        refreshPolicies(),
        refreshConfigSummary(),
        refreshAliases().catch(() => undefined),
        refreshAddressGroups().catch(() => undefined),
        refreshServices().catch(() => undefined),
        refreshNatRules().catch(() => undefined),
        refreshSchedules().catch(() => undefined),
      ]);
      setStatus({ text: `${result.ruleCount} règles indexées dans SQLite.`, level: "info" });
    } catch (err) {
      setStatus({ text: err instanceof Error ? err.message : String(err), level: "danger" });
    } finally {
      setBusy(false);
    }
  }

  async function refreshConfigTree() {
    const result = await callTool<ConfigTreeResult>("watchguard_config_tree", { query: ruleQuery() }, 20_000);
    setConfigTree(result);
  }

  async function openRule(name: string | null | undefined) {
    if (!name) return;
    setSelectedRuleName(name);
    setSelectedRule(null);
    setActiveTab("rules");
    setWgPmTab("policies");
    // Les lignes du Traffic Monitor portent le nom feuille (ex. "...-00") ou le nom policy.
    // On tente d'abord le nouveau détail Policy Manager (résolution d'alias + NAT + proxy).
    const matched = wgPolicies().find(
      (p) => p.name.toLowerCase() === name.toLowerCase() || (p.leafName ?? "").toLowerCase() === name.toLowerCase(),
    );
    if (matched) {
      await openPolicy(matched);
      return;
    }
    try {
      const result = await callTool<RuleDetailResult>(
        "watchguard_rule_get",
        { sessionId: session()?.sessionId, name },
        session() ? 35_000 : 15_000,
      );
      setSelectedRule(result);
    } catch (err) {
      setSelectedRule({
        rule: rules().find((r) => r.name.toLowerCase() === name.toLowerCase()) ?? null,
        liveOutput: err instanceof Error ? err.message : String(err),
        source: "error",
        relatedTraffic: [],
      });
    }
  }

  async function refreshTraffic() {
    // En mode live, on n'interroge jamais le backend : les deltas alimentent déjà
    // `trafficRows` en continu, et le filtrage se fait instantanément côté UI via le
    // memo `filteredTraffic`. Une recherche backend remplacerait le flux par une liste
    // pré-filtrée et casserait la cohérence avec les deltas suivants.
    if (trafficJobId()) return;
    const result = await callTool<TrafficQueryResult>("watchguard_traffic_query", { query: "", limit: 600 }, 20_000);
    setTrafficRows(result.rows);
    void refreshTrafficStats();
  }

  async function refreshTrafficStats() {
    try {
      const stats = await callTool<TrafficStatsResult>("watchguard_traffic_stats", { limit: 2000 }, 15_000);
      setTrafficStats(stats);
    } catch {
      // Les stats sont optionnelles : on ne bloque pas l'affichage du trafic.
    }
  }

  function subscribeTraffic(jobId: string) {
    wgOffTraffic?.();
    let statTimer: ReturnType<typeof setInterval> | null = null;
    wgOffTraffic = ws.on(
      (m) =>
        isWsEnvelope(m, "live", "watchguard", "traffic.delta") &&
        (m as { payload?: TrafficDeltaPayload }).payload?.jobId === jobId,
      (m) => {
        const payload = (m as { payload?: TrafficDeltaPayload }).payload;
        const rows = payload?.rows ?? [];
        if (!rows.length) return;
        setTrafficRows((cur) => uniqueById([...rows, ...cur]).slice(0, MAX_TRAFFIC_ROWS));
        // Incrémentation live des KPIs : on ne veut pas attendre le rafraîchissement
        // périodique (15 s) pour voir les compteurs allow/deny/catégorie bouger.
        setTrafficStats((cur) => bumpTrafficStats(cur, rows));
      },
    );
    // On rafraîchit les stats agrégées complètes toutes les ~15s pendant le live
    // (re-synchronise les tops sources/dest/apps que l'incrément local ne calcule pas).
    statTimer = setInterval(() => void refreshTrafficStats(), 15_000);
    const baseOff = wgOffTraffic;
    wgOffTraffic = () => {
      baseOff?.();
      if (statTimer) clearInterval(statTimer);
    };
  }

  // Incrémente localement les compteurs globaux (allow/deny/catégories/total) à partir des
  // deltas reçus en live. Les tops (sources/dest/apps) ne sont recalculés que côté backend.
  function bumpTrafficStats(cur: TrafficStatsResult | null, rows: TrafficRow[]): TrafficStatsResult {
    const base: TrafficStatsResult = cur ?? {
      total: 0,
      filter: 0,
      proxy: 0,
      status: 0,
      allow: 0,
      deny: 0,
      byProcId: {},
      byMsgId: {},
      topSrc: [],
      topDst: [],
      topPolicy: [],
      topApp: [],
      earliestMs: null,
      latestMs: null,
    };
    let filter = base.filter;
    let proxy = base.proxy;
    let status = base.status;
    let allow = base.allow;
    let deny = base.deny;
    const byProcId = { ...base.byProcId };
    for (const r of rows) {
      const cat = r.category ?? "filter";
      if (cat === "filter") filter++;
      else if (cat === "proxy") proxy++;
      else if (cat === "status") status++;
      const act = (r.action ?? "").toLowerCase();
      if (act.includes("allow") || act.includes("permit")) allow++;
      else if (act.includes("deny") || act.includes("drop") || act.includes("block")) deny++;
      if (r.procId) byProcId[r.procId] = (byProcId[r.procId] ?? 0) + 1;
    }
    return {
      ...base,
      total: base.total + rows.length,
      filter,
      proxy,
      status,
      allow,
      deny,
      byProcId,
      latestMs: rows.length ? (rows[0].timeMs ?? base.latestMs) : base.latestMs,
    };
  }

  async function startTraffic() {
    const s = session();
    if (!s) return;
    setBusy(true);
    try {
      // On précharge l'historique récent du cache AVANT d'activer le live : comme ça
      // l'opérateur garde le contexte (dernières lignes) et voit les nouveaux deltas
      // s'ajouter en haut plutôt que de partir d'une table vide.
      try {
        const cache = await callTool<TrafficQueryResult>("watchguard_traffic_query", { query: "", limit: 400 }, 20_000);
        setTrafficRows(cache.rows);
      } catch {
        // Cache indisponible : on continue quand même, le live remplira la table.
      }
      const result = await callTool<TrafficStartResult>(
        "watchguard_traffic_start",
        { sessionId: s.sessionId, intervalMs: 3_000, tail: 250 },
        15_000,
      );
      setTrafficJobId(result.jobId);
      subscribeTraffic(result.jobId);
      void refreshTrafficStats();
      setStatus({ text: `Traffic Monitor live toutes les ${Math.round(result.intervalMs / 1000)} s.`, level: "info" });
    } catch (err) {
      setStatus({ text: err instanceof Error ? err.message : String(err), level: "danger" });
    } finally {
      setBusy(false);
    }
  }

  async function stopTraffic() {
    const jobId = trafficJobId();
    wgOffTraffic?.();
    wgOffTraffic = null;
    setTrafficJobId(null);
    if (!jobId) return;
    try {
      await callTool("watchguard_traffic_stop", { jobId }, 10_000);
      setStatus({ text: "Traffic Monitor arrêté.", level: "info" });
    } catch {
      /* arrêt best-effort */
    }
  }

  function subscribeTcpdump(jobId: string) {
    wgOffTcpdump?.();
    wgOffTcpdump = ws.on(
      (m) =>
        isWsEnvelope(m, "live", "plugin", "job.event") &&
        (m as { payload?: { pluginId?: string; jobId?: string } }).payload?.pluginId === "watchguard" &&
        (m as { payload?: { jobId?: string } }).payload?.jobId === jobId,
      (m) => {
        const event = (
          m as { payload?: { event?: { type?: string; line?: string; result?: { lines?: string[] }; error?: string } } }
        ).payload?.event;
        if (!event) return;
        if (event.type === "log" && event.line) {
          setTcpdumpLines((cur) => [...cur, event.line!].slice(-500));
        }
        if (event.type === "done") {
          setTcpdumpRunning(false);
          setTcpdumpJobId(null);
          setTcpdumpLines(event.result?.lines ?? []);
          setStatus({ text: "tcpdump terminé.", level: "info" });
        }
        if (event.type === "error") {
          setTcpdumpRunning(false);
          setTcpdumpJobId(null);
          setStatus({ text: event.error ?? "tcpdump en erreur", level: "danger" });
        }
      },
    );
  }

  async function startTcpdump() {
    const s = session();
    if (!s) return;
    setBusy(true);
    setTcpdumpLines([]);
    try {
      const result = await callTool<TcpdumpStartResult>(
        "watchguard_tcpdump_start",
        {
          sessionId: s.sessionId,
          preset: tcpdumpPreset(),
          target: tcpdumpTarget() || undefined,
          port: tcpdumpPort() ? Number(tcpdumpPort()) : undefined,
          count: 20,
          snaplen: 160,
        },
        20_000,
      );
      setTcpdumpJobId(result.jobId);
      setTcpdumpRunning(true);
      setTcpdumpCommand(result.command);
      subscribeTcpdump(result.jobId);
      setStatus({ text: `tcpdump lancé : ${result.command}`, level: "info" });
    } catch (err) {
      setStatus({ text: err instanceof Error ? err.message : String(err), level: "danger" });
    } finally {
      setBusy(false);
    }
  }

  async function stopTcpdump() {
    const jobId = tcpdumpJobId();
    try {
      await callTool("watchguard_tcpdump_stop", { jobId }, 10_000);
    } catch {
      /* best-effort */
    }
    setTcpdumpRunning(false);
    setTcpdumpJobId(null);
    wgOffTcpdump?.();
    wgOffTcpdump = null;
  }

  async function refreshTcpdumpStatus() {
    const result = await callTool<TcpdumpStatusResult>("watchguard_tcpdump_status", {}, 10_000);
    setTcpdumpRunning(result.running);
    setTcpdumpJobId(result.jobId);
    setTcpdumpCommand(result.command);
  }

  async function refreshCache() {
    const result = await callTool<CacheStatsResult>("watchguard_cache_stats", {}, 10_000);
    setCacheStats(result);
  }

  // --- Chargement Policy Manager ---

  async function refreshPolicies() {
    const result = await callTool<FirewallPoliciesResult>(
      "watchguard_policies_list",
      { query: wgPolicyQuery(), limit: 2_000 },
      30_000,
    );
    setWgPolicies(result.policies);
    setWgPoliciesMeta({ snapshotId: result.snapshotId, snapshotAgeMs: result.snapshotAgeMs, view: result.view });
  }

  async function openPolicy(policy: FirewallPolicy) {
    const key = `${policy.order}:${policy.name}`;
    setWgPolicyKey(key);
    setWgPolicyDetail(null);
    setWgPmTab("policies");
    try {
      const result = await callTool<PolicyDetailResult>(
        "watchguard_policy_get",
        { sessionId: session()?.sessionId, order: policy.order },
        session() ? 35_000 : 15_000,
      );
      setWgPolicyDetail(result);
    } catch (err) {
      setWgPolicyDetail({
        policy,
        serviceEntry: null,
        natRule: null,
        proxyAction: null,
        scheduleEntry: null,
        fromTree: [],
        toTree: [],
        relatedTraffic: [],
        liveOutput: err instanceof Error ? err.message : String(err),
        source: "error",
      });
    }
  }

  async function refreshConfigSummary() {
    try {
      const result = await callTool<FirewareConfigSummary>("watchguard_config_summary", {}, 10_000);
      setWgConfigSummary(result);
    } catch {
      setWgConfigSummary(null);
    }
  }

  async function refreshAliases() {
    const result = await callTool<AliasesListResult>("watchguard_aliases_list", { query: "", limit: 5_000 }, 20_000);
    setWgAliases(result.aliases);
  }

  async function refreshAddressGroups() {
    const result = await callTool<AddressGroupsListResult>(
      "watchguard_address_groups_list",
      { query: "", limit: 5_000 },
      20_000,
    );
    setWgAddressGroups(result.groups);
  }

  async function refreshServices() {
    const result = await callTool<ServicesListResult>("watchguard_services_list", { query: "", limit: 5_000 }, 20_000);
    setWgServices(result.services);
  }

  async function refreshNatRules() {
    const result = await callTool<NatRulesListResult>("watchguard_nat_rules_list", { query: "", limit: 2_000 }, 20_000);
    setWgNatRules(result);
  }

  async function refreshSchedules() {
    const result = await callTool<SchedulesListResult>(
      "watchguard_schedules_list",
      { query: "", limit: 2_000 },
      20_000,
    );
    setWgSchedules(result.schedules);
  }

  async function refreshPmTab(tab: PmTabId) {
    setWgPmTab(tab);
    if (tab === "aliases" && !wgAliases().length) await refreshAliases().catch(() => undefined);
    if (tab === "services" && !wgServices().length) await refreshServices().catch(() => undefined);
    if (tab === "nat" && !wgNatRules()) await refreshNatRules().catch(() => undefined);
    if (tab === "schedules" && !wgSchedules().length) await refreshSchedules().catch(() => undefined);
  }

  onMount(() => {
    void refreshProbe();
    void refreshRules().catch(() => undefined);
    void refreshTraffic().catch(() => undefined);
    void refreshConfigTree().catch(() => undefined);
    void refreshTcpdumpStatus().catch(() => undefined);
    void refreshCache().catch(() => undefined);
    void refreshPolicies().catch(() => undefined);
    void refreshConfigSummary().catch(() => undefined);
  });

  onCleanup(() => {
    void stopTraffic();
    wgOffTcpdump?.();
  });

  const toolbar = (
    <div class="wg-toolbar">
      <div class="wg-login">
        {/* ── CLI Fireware : composant d'identification générique ── */}
        <CredentialInput
          usage="watchguard-cli"
          vaultDefaults={props.vaultDefaults}
          layout="inline"
          fields={{ username: true, password: true, domain: false }}
          defaults={{ username: "status" }}
          disabled={connected() || busy()}
          requestAgentKey={requestCredentialKeyOnce}
          onResult={(r) => setCliResult(r)}
        />
        <label class="wg-check">
          <input
            type="checkbox"
            checked={portalEnabled()}
            disabled={connected() || busy()}
            onChange={(e) => setPortalEnabled(e.currentTarget.checked)}
          />
          <span>Portail 4100</span>
        </label>
        <Show when={portalEnabled()}>
          {/* ── Portail 4100 : composant d'identification générique ── */}
          <CredentialInput
            usage="watchguard-portal"
            vaultDefaults={props.vaultDefaults}
            layout="inline"
            fields={{ username: true, password: true, domain: "optional" }}
            defaults={{ domain: "Firebox-DB" }}
            disabled={connected() || busy()}
            requestAgentKey={requestCredentialKeyOnce}
            onResult={(r) => setPortalResult(r)}
          />
          <input
            aria-label="Port portail WatchGuard"
            placeholder="port"
            inputMode="numeric"
            value={portalPort()}
            onInput={(e) => setPortalPort(e.currentTarget.value)}
            disabled={connected() || busy()}
          />
        </Show>
        <Show
          when={connected()}
          fallback={
            <button type="button" class="wg-primary" disabled={busy() || !canUseTools()} onClick={() => void login()}>
              Connecter
            </button>
          }
        >
          <button type="button" disabled={busy()} onClick={() => void logout()}>
            Déconnecter
          </button>
        </Show>
      </div>
      <button type="button" onClick={() => void refreshProbe()} disabled={busy()}>
        Probe
      </button>
    </div>
  );

  return (
    <PanelLayout title={props.title ?? "WatchGuard Firebox"} status={status()} toolbar={toolbar}>
      <div class="wg-shell">
        <section class="wg-top">
          <div class="wg-kpi">
            <span class="wg-kpi-label">Passerelle</span>
            <strong>{probe()?.gateway ?? "non détectée"}</strong>
            <small>{probe()?.interfaceName ?? probe()?.interfaceDescription ?? ""}</small>
          </div>
          <div class="wg-kpi" data-state={probe()?.reachable ? "ok" : "warn"}>
            <span class="wg-kpi-label">CLI 4118</span>
            <strong>{probe()?.reachable ? "joignable" : "à vérifier"}</strong>
            <small>{probe()?.error ?? `${probe()?.latencyMs ?? 0} ms`}</small>
          </div>
          <div class="wg-kpi">
            <span class="wg-kpi-label">Session</span>
            <strong>{session()?.role ?? "hors ligne"}</strong>
            <small>{session() ? `${session()!.username}@${session()!.gateway}` : "compte status recommandé"}</small>
          </div>
          <div class="wg-kpi">
            <span class="wg-kpi-label">Cache SQLite</span>
            <strong>{cacheStats()?.traffic ?? 0} logs</strong>
            <small>{cacheStats()?.rules ?? rulesMeta()?.count ?? 0} règles</small>
          </div>
        </section>

        <Show when={!props.lockedTab}>
          <nav class="wg-tabs" aria-label="WatchGuard">
            <button type="button" aria-selected={activeTab() === "rules"} onClick={() => setActiveTab("rules")}>
              Règles
            </button>
            <button type="button" aria-selected={activeTab() === "traffic"} onClick={() => setActiveTab("traffic")}>
              Traffic Monitor
            </button>
            <button type="button" aria-selected={activeTab() === "links"} onClick={() => setActiveTab("links")}>
              Link monitors
            </button>
            <button type="button" aria-selected={activeTab() === "users"} onClick={() => setActiveTab("users")}>
              Authentifiés
            </button>
            <button type="button" aria-selected={activeTab() === "config"} onClick={() => setActiveTab("config")}>
              Config
            </button>
            <button type="button" aria-selected={activeTab() === "tcpdump"} onClick={() => setActiveTab("tcpdump")}>
              tcpdump
            </button>
            <button type="button" aria-selected={activeTab() === "cache"} onClick={() => setActiveTab("cache")}>
              Cache
            </button>
          </nav>
        </Show>

        <main class="wg-main">
          <section class="wg-content">
            <Show when={activeTab() === "rules"}>
              <div class="wg-pm-head">
                <div class="wg-pm-subtabs">
                  <button type="button" aria-selected={wgPmTab() === "policies"} onClick={() => setWgPmTab("policies")}>
                    Policies
                  </button>
                  <button
                    type="button"
                    aria-selected={wgPmTab() === "aliases"}
                    onClick={() => void refreshPmTab("aliases")}
                  >
                    Aliases
                  </button>
                  <button
                    type="button"
                    aria-selected={wgPmTab() === "services"}
                    onClick={() => void refreshPmTab("services")}
                  >
                    Services
                  </button>
                  <button type="button" aria-selected={wgPmTab() === "nat"} onClick={() => void refreshPmTab("nat")}>
                    NAT
                  </button>
                  <button
                    type="button"
                    aria-selected={wgPmTab() === "schedules"}
                    onClick={() => void refreshPmTab("schedules")}
                  >
                    Schedules
                  </button>
                </div>
                <Show when={wgConfigSummary()}>
                  {(s) => (
                    <span class="wg-muted wg-pm-meta">
                      Fireware {s().forVersion ?? "?"} · vue {s().view ?? "?"} · geo{" "}
                      {s().geolocation.enabled ? "on" : "off"}
                    </span>
                  )}
                </Show>
              </div>

              <Show when={wgPmTab() === "policies"}>
                <div class="wg-actions">
                  <input
                    placeholder="Filtrer policy, source, destination, service, IP"
                    value={wgPolicyQuery()}
                    onInput={(e) => setWgPolicyQuery(e.currentTarget.value)}
                  />
                  <button type="button" onClick={() => void refreshPolicies()} disabled={busy()}>
                    Filtrer
                  </button>
                  <button type="button" onClick={() => void refreshConfig()} disabled={busy() || !connected()}>
                    Rafraîchir config
                  </button>
                  <span class="wg-muted">
                    {wgPolicies().length} policies · ordre d'évaluation · snapshot{" "}
                    {wgPoliciesMeta()?.snapshotId ?? "n/a"} · {fmtAge(wgPoliciesMeta()?.snapshotAgeMs ?? -1)}
                  </span>
                </div>
                <div class="wg-table-wrap">
                  <table class="wg-table wg-pm-table">
                    <thead>
                      <tr>
                        <th class="wg-order-cell">#</th>
                        <th>Action</th>
                        <th>Policy</th>
                        <th>Type</th>
                        <th>From</th>
                        <th>To</th>
                        <th>NAT</th>
                        <th>Proxy</th>
                      </tr>
                    </thead>
                    <tbody>
                      <For each={filteredPolicies()}>
                        {(p) => {
                          const g = pmActionGlyph(p.action);
                          return (
                            <tr
                              classList={{
                                "is-selected": wgPolicyKey() === `${p.order}:${p.name}`,
                                "wg-row-disabled": !p.enabled,
                              }}
                              onClick={() => void openPolicy(p)}
                            >
                              <td class="wg-order-cell">{p.order}</td>
                              <td>
                                <span class="wg-pm-action-icon" data-tone={g.tone} title={g.label}>
                                  {g.icon}
                                </span>
                                <span class="wg-pm-action-label">{g.label}</span>
                                <span class="wg-pm-badges">
                                  <Show when={p.log}>
                                    <span class="wg-status-badge" title="Log">
                                      🔔
                                    </span>
                                  </Show>
                                  <Show when={p.ips}>
                                    <span class="wg-status-badge" title="IPS">
                                      🛡
                                    </span>
                                  </Show>
                                  <Show when={p.schedule && p.schedule !== "Always On"}>
                                    <span class="wg-status-badge" title={`Schedule ${p.schedule}`}>
                                      ⏰
                                    </span>
                                  </Show>
                                  <Show when={p.geoAction && p.geoAction !== "Global"}>
                                    <span class="wg-status-badge" title={`Geo ${p.geoAction}`}>
                                      🌍
                                    </span>
                                  </Show>
                                  <Show when={p.appAction}>
                                    <span class="wg-status-badge" title={`App Control ${p.appAction}`}>
                                      📋
                                    </span>
                                  </Show>
                                  <Show when={p.sdwanAction}>
                                    <span class="wg-status-badge" title={`SD-WAN ${p.sdwanAction}`}>
                                      🔀
                                    </span>
                                  </Show>
                                  <Show when={p.tlsSetting}>
                                    <span class="wg-status-badge" title={`TLS ${p.tlsSetting}`}>
                                      🔒
                                    </span>
                                  </Show>
                                </span>
                              </td>
                              <td>
                                <button
                                  type="button"
                                  class="wg-link-button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    void openPolicy(p);
                                  }}
                                >
                                  {p.name}
                                </button>
                                <Show when={p.description}>
                                  <small class="wg-muted"> {p.description}</small>
                                </Show>
                                <Show when={p.tags.length > 0}>
                                  <div class="wg-tag-row">
                                    <For each={p.tags}>{(tag) => <span class="wg-pill wg-tag">{tag}</span>}</For>
                                  </div>
                                </Show>
                              </td>
                              <td>{p.service ?? p.type ?? "—"}</td>
                              <td>
                                <span title={p.from.join(", ")}>{joinShort(p.fromResolved, "—")}</span>
                              </td>
                              <td>
                                <span title={p.to.join(", ")}>{joinShort(p.toResolved, "—")}</span>
                              </td>
                              <td>
                                <Show when={p.nat}>
                                  <span class="wg-pill">{p.nat}</span>
                                </Show>
                                <Show when={p.applyOneToOneNat}>
                                  <span class="wg-status-badge" title="1:1 NAT">
                                    1:1
                                  </span>
                                </Show>
                                <Show when={p.applyDnatGlobal}>
                                  <span class="wg-status-badge" title="DNAT global">
                                    DNAT
                                  </span>
                                </Show>
                              </td>
                              <td>{p.proxy ?? "—"}</td>
                            </tr>
                          );
                        }}
                      </For>
                    </tbody>
                  </table>
                </div>
              </Show>

              <Show when={wgPmTab() === "aliases"}>
                <div class="wg-actions">
                  <span class="wg-muted">
                    {wgAliases().length} aliases · {wgAddressGroups().length} groupes d'adresses
                  </span>
                  <button type="button" onClick={() => void refreshAddressGroups()} disabled={busy()}>
                    Rafraîchir
                  </button>
                </div>
                <div class="wg-table-wrap">
                  <table class="wg-table">
                    <thead>
                      <tr>
                        <th>Alias</th>
                        <th>Description</th>
                        <th>Membres résolus</th>
                      </tr>
                    </thead>
                    <tbody>
                      <For each={wgAliases()}>
                        {(a) => (
                          <tr>
                            <td>
                              <strong>{a.name}</strong>
                            </td>
                            <td>{a.description ?? "—"}</td>
                            <td>{joinShort(a.resolved, "—")}</td>
                          </tr>
                        )}
                      </For>
                    </tbody>
                  </table>
                </div>
              </Show>

              <Show when={wgPmTab() === "services"}>
                <div class="wg-actions">
                  <span class="wg-muted">{wgServices().length} services</span>
                  <button type="button" onClick={() => void refreshServices()} disabled={busy()}>
                    Rafraîchir
                  </button>
                </div>
                <div class="wg-table-wrap">
                  <table class="wg-table">
                    <thead>
                      <tr>
                        <th>Service</th>
                        <th>Description</th>
                        <th>Protocole / port</th>
                      </tr>
                    </thead>
                    <tbody>
                      <For each={wgServices()}>
                        {(s) => (
                          <tr>
                            <td>
                              <strong>{s.name}</strong>
                            </td>
                            <td>{s.description ?? "—"}</td>
                            <td>
                              {s.items.map((i) => `${i.protocol}${i.port ? `/${i.port}` : ""}`).join(", ") || "—"}
                            </td>
                          </tr>
                        )}
                      </For>
                    </tbody>
                  </table>
                </div>
              </Show>

              <Show when={wgPmTab() === "nat"}>
                <div class="wg-actions">
                  <span class="wg-muted">
                    {wgNatRules()?.natRules.length ?? 0} règles NAT · {wgNatRules()?.dnat.length ?? 0} DNAT
                  </span>
                  <button type="button" onClick={() => void refreshNatRules()} disabled={busy()}>
                    Rafraîchir
                  </button>
                </div>
                <div class="wg-table-wrap">
                  <table class="wg-table">
                    <thead>
                      <tr>
                        <th>NAT rule</th>
                        <th>Type</th>
                        <th>Ext / interface / cible</th>
                      </tr>
                    </thead>
                    <tbody>
                      <For each={wgNatRules()?.natRules ?? []}>
                        {(n) => (
                          <tr>
                            <td>
                              <strong>{n.name}</strong>
                            </td>
                            <td>{n.type}</td>
                            <td>
                              <For each={n.items}>
                                {(it) => (
                                  <div class="wg-muted">
                                    {it.extAddrName ?? "?"} @ {it.interface ?? "?"} → {it.addrName ?? "?"}{" "}
                                    {it.addrResolved.length ? `(${it.addrResolved.join(", ")})` : ""}
                                    {it.port ? `:${it.port}` : ""}
                                  </div>
                                )}
                              </For>
                            </td>
                          </tr>
                        )}
                      </For>
                    </tbody>
                  </table>
                </div>
                <Show when={(wgNatRules()?.dnat.length ?? 0) > 0}>
                  <h4>DNAT global</h4>
                  <For each={wgNatRules()?.dnat ?? []}>
                    {(d) => (
                      <div class="wg-muted">
                        {d.fromAlias} → {d.toAlias}
                      </div>
                    )}
                  </For>
                </Show>
              </Show>

              <Show when={wgPmTab() === "schedules"}>
                <div class="wg-actions">
                  <span class="wg-muted">{wgSchedules().length} planifications</span>
                  <button type="button" onClick={() => void refreshSchedules()} disabled={busy()}>
                    Rafraîchir
                  </button>
                </div>
                <div class="wg-table-wrap">
                  <table class="wg-table">
                    <thead>
                      <tr>
                        <th>Schedule</th>
                        <th>Description</th>
                        <th>Résumé</th>
                      </tr>
                    </thead>
                    <tbody>
                      <For each={wgSchedules()}>
                        {(s) => (
                          <tr>
                            <td>
                              <strong>{s.name}</strong>
                            </td>
                            <td>{s.description ?? "—"}</td>
                            <td>{s.summary}</td>
                          </tr>
                        )}
                      </For>
                    </tbody>
                  </table>
                </div>
              </Show>
            </Show>

            <Show when={activeTab() === "traffic"}>
              <div class="wg-actions">
                <input
                  placeholder="Filtrer policy, IP, SNI, app, user, msg…"
                  value={trafficQuery()}
                  onInput={(e) => setTrafficQuery(e.currentTarget.value)}
                />
                <div class="wg-seg" role="tablist" aria-label="Catégorie de trafic">
                  <For each={["all", "filter", "proxy", "status"] as const}>
                    {(c) => (
                      <button
                        type="button"
                        role="tab"
                        aria-selected={trafficCategory() === c}
                        onClick={() => setTrafficCategory(c)}
                      >
                        {c === "all" ? "Tout" : trafficCategoryLabel(c)}
                      </button>
                    )}
                  </For>
                </div>
                <Show when={!trafficJobId()} fallback={<span class="wg-muted">Filtres appliqués en direct</span>}>
                  <button type="button" onClick={() => void refreshTraffic()} disabled={busy()}>
                    Rechercher cache
                  </button>
                </Show>
                <Show
                  when={trafficJobId()}
                  fallback={
                    <button
                      type="button"
                      class="wg-primary"
                      onClick={() => void startTraffic()}
                      disabled={busy() || !connected()}
                    >
                      ▶ Live
                    </button>
                  }
                >
                  <span class="wg-live-badge" title="Flux temps réel depuis la passerelle">
                    <span class="wg-live-dot" /> LIVE
                  </span>
                  <button type="button" onClick={() => void stopTraffic()}>
                    ■ Stop live
                  </button>
                </Show>
                <span class="wg-muted">{filteredTraffic().length} lignes affichées</span>
              </div>

              <Show when={trafficStats()}>
                {(s) => (
                  <div class="wg-tstats">
                    <div class="wg-tstat" data-tone="allow">
                      <span class="wg-tstat-num">{s().allow}</span>
                      <span class="wg-tstat-lbl">Allow</span>
                    </div>
                    <div class="wg-tstat" data-tone="deny">
                      <span class="wg-tstat-num">{s().deny}</span>
                      <span class="wg-tstat-lbl">Deny</span>
                    </div>
                    <div class="wg-tstat">
                      <span class="wg-tstat-num">{s().filter}</span>
                      <span class="wg-tstat-lbl">🛡 Filtrage</span>
                    </div>
                    <div class="wg-tstat">
                      <span class="wg-tstat-num">{s().proxy}</span>
                      <span class="wg-tstat-lbl">⚙ Proxy</span>
                    </div>
                    <div class="wg-tstat">
                      <span class="wg-tstat-num">{s().status}</span>
                      <span class="wg-tstat-lbl">ℹ Système</span>
                    </div>
                    <Show when={s().topApp.length > 0}>
                      <div class="wg-tstat wg-tstat-wide">
                        <span class="wg-tstat-lbl">Top apps</span>
                        <span class="wg-tstat-tags">
                          <For each={s().topApp.slice(0, 5)}>
                            {(a) => (
                              <button
                                type="button"
                                class="wg-chip"
                                title={`${a.count} occurrences`}
                                onClick={() => setTrafficQuery(a.label)}
                              >
                                {a.label} <em>{a.count}</em>
                              </button>
                            )}
                          </For>
                        </span>
                      </div>
                    </Show>
                    <Show when={s().topDst.length > 0}>
                      <div class="wg-tstat wg-tstat-wide">
                        <span class="wg-tstat-lbl">Top destinations</span>
                        <span class="wg-tstat-tags">
                          <For each={s().topDst.slice(0, 5)}>
                            {(d) => (
                              <button
                                type="button"
                                class="wg-chip"
                                title={`${d.count} occurrences`}
                                onClick={() => setTrafficQuery(d.label)}
                              >
                                {d.label} <em>{d.count}</em>
                              </button>
                            )}
                          </For>
                        </span>
                      </div>
                    </Show>
                  </div>
                )}
              </Show>

              <div class="wg-table-wrap">
                <table class="wg-table wg-table-traffic">
                  <thead>
                    <tr>
                      <th>Heure</th>
                      <th>Type</th>
                      <th>Action</th>
                      <th>Policy</th>
                      <th>Service</th>
                      <th>Source</th>
                      <th>Destination</th>
                      <th>Détections</th>
                      <th>Détail</th>
                    </tr>
                  </thead>
                  <tbody>
                    <For each={filteredTraffic().slice(0, 400)}>
                      {(row) => {
                        const detail = trafficRowDetail(row);
                        const svc = serviceGlyph(row.proto, row.dstPort);
                        const badges = detectionBadges(row);
                        const counters = [
                          row.sentBytes != null ? `↑${fmtBytes(row.sentBytes)}` : null,
                          row.rcvdBytes != null ? `↓${fmtBytes(row.rcvdBytes)}` : null,
                        ]
                          .filter(Boolean)
                          .join(" ");
                        return (
                          <tr
                            data-cat={row.category ?? "filter"}
                            data-tone={actionTone(row.action)}
                            onClick={() => setSelectedTraffic(row)}
                          >
                            <td class="wg-td-time">{fmtTime(row.time)}</td>
                            <td>
                              <span
                                class="wg-cat"
                                data-cat={row.category ?? "filter"}
                                title={trafficCategoryLabel(row.category)}
                              >
                                {trafficCategoryIcon(row.category)}
                              </span>
                              <Show when={row.severity && row.severity !== "info" && row.severity !== "debug"}>
                                <span class="wg-sev" data-sev={row.severity} title={`Priorité ${row.severity}`}>
                                  ⚠
                                </span>
                              </Show>
                            </td>
                            <td>
                              <span class="wg-action" data-tone={actionTone(row.action)}>
                                <span class="wg-action-sym">{actionSymbol(row.action, row.category)}</span>
                                {row.action ?? "—"}
                              </span>
                            </td>
                            <td>
                              <Show when={row.policy} fallback={<span class="wg-muted">{row.procId ?? "n/a"}</span>}>
                                <button
                                  type="button"
                                  class="wg-link-button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    void openRule(row.policy!);
                                  }}
                                >
                                  {row.policy}
                                </button>
                              </Show>
                            </td>
                            <td class="wg-td-svc" title={`${(svc.label || row.proto) ?? ""}`}>
                              <span class="wg-svc-icon">{svc.icon}</span>
                              <span class="wg-svc-label">{(svc.label || row.proto) ?? ""}</span>
                            </td>
                            <td class="wg-td-host">
                              <div>{fmtHost(row.src, row.srcPort) || <span class="wg-muted">—</span>}</div>
                              <Show when={row.srcIntf}>
                                <div class="wg-intf">{row.srcIntf}</div>
                              </Show>
                              <Show when={row.srcIpNat}>
                                <div class="wg-nat" title="Source après NAT">
                                  ↔ {fmtHost(row.srcIpNat, row.srcPortNat)}
                                </div>
                              </Show>
                            </td>
                            <td class="wg-td-host">
                              <div>{fmtHost(row.dst, row.dstPort) || <span class="wg-muted">—</span>}</div>
                              <Show when={row.sni || row.dstName}>
                                <div class="wg-sni" title="SNI / hôte proxy">
                                  {row.sni ?? row.dstName}
                                </div>
                              </Show>
                              <Show when={row.dstIntf && !row.sni && !row.dstName}>
                                <div class="wg-intf">{row.dstIntf}</div>
                              </Show>
                            </td>
                            <td class="wg-td-detect">
                              <Show when={badges.length} fallback={<span class="wg-muted">—</span>}>
                                <For each={badges}>
                                  {(b) => (
                                    <span class="wg-detect-badge" data-tone={b.tone} title={b.label}>
                                      {b.symbol}
                                    </span>
                                  )}
                                </For>
                              </Show>
                            </td>
                            <td class="wg-td-detail">
                              {detail}
                              <Show when={counters}>
                                <span class="wg-detail-cnt"> · {counters}</span>
                              </Show>
                            </td>
                          </tr>
                        );
                      }}
                    </For>
                  </tbody>
                </table>
              </div>

              <Show when={selectedTraffic()}>
                {(row) => (
                  <div
                    class="wg-modal-overlay"
                    role="dialog"
                    aria-modal="true"
                    aria-label="Détail du log"
                    tabIndex={-1}
                    ref={(el) => setTimeout(() => el.focus(), 10)}
                    onMouseDown={(e) => {
                      if (e.target === e.currentTarget) setSelectedTraffic(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Escape") {
                        e.preventDefault();
                        setSelectedTraffic(null);
                      }
                    }}
                  >
                    <div class="wg-modal-card">
                      <header class="wg-modal-header">
                        <div class="wg-modal-title">
                          <span class="wg-cat wg-cat-lg" data-cat={row().category ?? "filter"}>
                            {trafficCategoryIcon(row().category)}
                          </span>
                          <div>
                            <strong>
                              {row().logTag ?? "Log"} · {trafficCategoryLabel(row().category)}
                            </strong>
                            <div class="wg-modal-subtitle">
                              <Show when={row().action}>
                                <span class="wg-action" data-tone={actionTone(row().action)}>
                                  {actionSymbol(row().action, row().category)} {row().action}
                                </span>
                              </Show>
                              <Show when={row().severity}>
                                <span class="wg-sev-inline" data-sev={row().severity}>
                                  priorité {row().severity}
                                </span>
                              </Show>
                              <Show when={row().msgId}>
                                <span class="wg-muted">
                                  msg_id <code>{row().msgId}</code>
                                </span>
                              </Show>
                              <span class="wg-muted">{fmtTime(row().time)}</span>
                            </div>
                          </div>
                        </div>
                        <button
                          type="button"
                          class="wg-link-button"
                          onClick={() => setSelectedTraffic(null)}
                          title="Fermer (Échap)"
                        >
                          ✕
                        </button>
                      </header>
                      <div class="wg-modal-body">
                        {/* Hero : phrase de synthèse adaptée au type de log. */}
                        {(() => {
                          const hero = trafficHero(row());
                          return (
                            <div class="wg-modal-hero" data-tone={hero.tone}>
                              {hero.text}
                            </div>
                          );
                        })()}
                        {/* 🛰 Réseau — masqué pour les événements système sans 5-tuple (FWStatus). */}
                        <Show when={row().src || row().dst}>
                          <section class="wg-modal-section">
                            <h4>🛰 Réseau</h4>
                            <dl class="wg-dl">
                              <Show when={row().policy}>
                                <dt>Policy</dt>
                                <dd>
                                  <button
                                    type="button"
                                    class="wg-link-button"
                                    onClick={() => void openRule(row().policy!)}
                                  >
                                    {row().policy}
                                  </button>
                                </dd>
                              </Show>
                              <Show when={row().src}>
                                <dt>Source</dt>
                                <dd>
                                  {fmtHost(row().src, row().srcPort)}
                                  <Show when={row().srcIntf}>
                                    <span class="wg-muted"> · {row().srcIntf}</span>
                                  </Show>
                                </dd>
                              </Show>
                              <Show when={row().dst}>
                                <dt>Destination</dt>
                                <dd>
                                  {fmtHost(row().dst, row().dstPort)}
                                  <Show when={row().dstIntf}>
                                    <span class="wg-muted"> · {row().dstIntf}</span>
                                  </Show>
                                </dd>
                              </Show>
                              <Show when={row().proto || row().dstPort != null}>
                                <dt>Service</dt>
                                <dd>
                                  {(() => {
                                    const svc = serviceGlyph(row().proto, row().dstPort);
                                    return `${svc.icon} ${svc.label}`;
                                  })()}
                                </dd>
                              </Show>
                              <Show when={row().user}>
                                <dt>Utilisateur</dt>
                                <dd>{row().user}</dd>
                              </Show>
                              <Show when={row().srcIntf && row().dstIntf}>
                                <dt>Chemin</dt>
                                <dd>
                                  {row().srcIntf} → {row().dstIntf}
                                </dd>
                              </Show>
                            </dl>
                          </section>
                        </Show>

                        {/* ↔ NAT — uniquement si translation source appliquée. */}
                        <Show when={row().srcIpNat}>
                          <section class="wg-modal-section">
                            <h4>↔ NAT</h4>
                            <dl class="wg-dl">
                              <dt>Source après NAT</dt>
                              <dd>
                                {fmtHost(row().srcIpNat, row().srcPortNat)}
                                <span class="wg-muted"> · route {row().routeType ?? "standard"}</span>
                              </dd>
                            </dl>
                          </section>
                        </Show>

                        {/* 📨 Requête DNS — uniquement pour les allow DNS. */}
                        <Show when={row().question}>
                          <section class="wg-modal-section">
                            <h4>📨 Requête DNS</h4>
                            <dl class="wg-dl">
                              <dt>Question</dt>
                              <dd class="wg-mono">{row().question}</dd>
                              <Show when={row().recordType}>
                                <dt>Type d'enregistrement</dt>
                                <dd>{row().recordType}</dd>
                              </Show>
                            </dl>
                          </section>
                        </Show>

                        {/* 🔄 Proxy & TLS — pour les actions proxy (HTTPSReq, SMTP…). */}
                        <Show when={row().proxyAct || row().proxyAction || row().tlsVersion || row().tlsProfile}>
                          <section class="wg-modal-section">
                            <h4>🔄 Proxy & TLS</h4>
                            <dl class="wg-dl">
                              <Show when={row().proxyAct}>
                                <dt>Action proxy</dt>
                                <dd>{row().proxyAct}</dd>
                              </Show>
                              <Show when={row().proxyAction}>
                                <dt>Résultat</dt>
                                <dd>{row().proxyAction}</dd>
                              </Show>
                              <Show when={row().sni || row().dstName}>
                                <dt>Hôte inspecté</dt>
                                <dd>{row().sni ?? row().dstName}</dd>
                              </Show>
                              <Show when={row().tlsVersion || row().tlsProfile}>
                                <dt>🔒 TLS</dt>
                                <dd>
                                  {[row().tlsVersion, row().tlsProfile].filter(Boolean).join(" · ")}
                                  <Show when={row().cn}>
                                    <span class="wg-muted"> · CN {row().cn}</span>
                                  </Show>
                                </dd>
                              </Show>
                              <Show when={row().certSubject}>
                                <dt>Certificat</dt>
                                <dd class="wg-mono">{row().certSubject}</dd>
                              </Show>
                              <Show when={row().certIssuer}>
                                <dt>Émetteur</dt>
                                <dd class="wg-mono">{row().certIssuer}</dd>
                              </Show>
                            </dl>
                          </section>
                        </Show>

                        {/* 📋 Application Control — si une application est identifiée. */}
                        <Show when={row().appName || row().appCatName || row().sigVers}>
                          <section class="wg-modal-section">
                            <h4>📋 Application Control</h4>
                            <dl class="wg-dl">
                              <Show when={row().appName}>
                                <dt>Application</dt>
                                <dd>{row().appName}</dd>
                              </Show>
                              <Show when={row().appCatName}>
                                <dt>Catégorie</dt>
                                <dd>{row().appCatName}</dd>
                              </Show>
                              <Show when={row().appBehName}>
                                <dt>Comportement</dt>
                                <dd>{row().appBehName}</dd>
                              </Show>
                              <Show when={row().sigVers}>
                                <dt>Signature</dt>
                                <dd>v{row().sigVers}</dd>
                              </Show>
                            </dl>
                          </section>
                        </Show>

                        {/* 🚫 WebBlocker — décision de filtrage de contenu. */}
                        <Show when={row().categories}>
                          <section class="wg-modal-section">
                            <h4>🚫 WebBlocker</h4>
                            <dl class="wg-dl">
                              <dt>Catégories</dt>
                              <dd>{row().categories}</dd>
                            </dl>
                          </section>
                        </Show>

                        {/* 📊 Trafic & compteurs — volume, durée, taille paquet. */}
                        <Show
                          when={
                            row().sentBytes != null ||
                            row().rcvdBytes != null ||
                            row().pcktLen != null ||
                            row().ttl != null
                          }
                        >
                          <section class="wg-modal-section">
                            <h4>📊 Trafic & compteurs</h4>
                            <dl class="wg-dl">
                              <Show when={row().sentBytes != null || row().rcvdBytes != null}>
                                <dt>Volume</dt>
                                <dd>
                                  <Show when={row().sentBytes != null}>↑ {fmtBytes(row().sentBytes)} </Show>
                                  <Show when={row().rcvdBytes != null}>↓ {fmtBytes(row().rcvdBytes)}</Show>
                                </dd>
                              </Show>
                              <Show when={row().sentPkts != null || row().rcvdPkts != null}>
                                <dt>Paquets</dt>
                                <dd>
                                  <Show when={row().sentPkts != null}>↑ {row().sentPkts} </Show>
                                  <Show when={row().rcvdPkts != null}>↓ {row().rcvdPkts}</Show>
                                </dd>
                              </Show>
                              <Show when={row().duration != null}>
                                <dt>Durée</dt>
                                <dd>{row().duration} ms</dd>
                              </Show>
                              <Show when={row().pcktLen != null}>
                                <dt>Taille paquet</dt>
                                <dd>{row().pcktLen} octets</dd>
                              </Show>
                              <Show when={row().ttl != null}>
                                <dt>TTL</dt>
                                <dd>{row().ttl}</dd>
                              </Show>
                            </dl>
                          </section>
                        </Show>

                        {/* 🔧 TCP — flags et état de connexion (typique des FWDeny). */}
                        <Show when={row().flags || row().tcpInfo}>
                          <section class="wg-modal-section">
                            <h4>🔧 TCP</h4>
                            <dl class="wg-dl">
                              <Show when={row().flags}>
                                <dt>Flags</dt>
                                <dd>
                                  <code>{row().flags}</code>
                                </dd>
                              </Show>
                              <Show when={row().tcpInfo}>
                                <dt>État</dt>
                                <dd class="wg-mono">{row().tcpInfo}</dd>
                              </Show>
                            </dl>
                          </section>
                        </Show>

                        {/* 📡 Événement système — message Fireware (link-mon, admd, etc.). */}
                        <Show when={row().message || (row().procId && !row().src && !row().dst)}>
                          <section class="wg-modal-section">
                            <h4>📡 Événement système</h4>
                            <dl class="wg-dl">
                              <Show when={row().procId}>
                                <dt>Processus</dt>
                                <dd>{row().procId}</dd>
                              </Show>
                              <Show when={row().logType}>
                                <dt>Type</dt>
                                <dd>
                                  {row().logType === "tr"
                                    ? "Trafic (tr)"
                                    : row().logType === "ev"
                                      ? "Événement (ev)"
                                      : row().logType === "db"
                                        ? "Debug (db)"
                                        : row().logType}
                                </dd>
                              </Show>
                              <Show when={row().message}>
                                <dt>Message</dt>
                                <dd>{row().message}</dd>
                              </Show>
                            </dl>
                          </section>
                        </Show>

                        <details class="wg-details">
                          <summary>Log brut Fireware</summary>
                          <pre class="wg-output wg-output-raw">{row().raw}</pre>
                          <button
                            type="button"
                            class="wg-link-button"
                            onClick={() => {
                              navigator.clipboard?.writeText(row().raw).catch(() => {});
                            }}
                          >
                            📋 Copier le log brut
                          </button>
                        </details>
                      </div>
                    </div>
                  </div>
                )}
              </Show>
            </Show>

            <Show when={activeTab() === "links"}>
              <div class="wg-actions">
                <button type="button" onClick={() => void refreshLinks()} disabled={busy() || !connected()}>
                  Vérifier link monitors
                </button>
                <span class="wg-muted">
                  {linkKpis().total} moniteurs · {linkKpis().up} up · {linkKpis().down} down
                </span>
              </div>
              <div class="wg-link-grid">
                <For each={linkMonitors()?.monitors ?? []}>
                  {(link) => (
                    <article class="wg-link-card" data-state={linkTone(link.status)}>
                      <header>
                        <strong>{link.name}</strong>
                        <span>{link.status ?? "état inconnu"}</span>
                      </header>
                      <dl>
                        <dt>Interface</dt>
                        <dd>{link.interfaceName ?? "n/a"}</dd>
                        <dt>Cible</dt>
                        <dd>{[link.method, link.target].filter(Boolean).join(" ") || "n/a"}</dd>
                        <dt>Mesures</dt>
                        <dd>{[link.latency, link.packetLoss, link.jitter].filter(Boolean).join(" · ") || "n/a"}</dd>
                      </dl>
                      <pre>{link.raw}</pre>
                    </article>
                  )}
                </For>
              </div>
              <Show when={!linkMonitors()?.monitors.length && linkMonitors()?.raw}>
                <pre class="wg-output">{linkMonitors()?.raw}</pre>
              </Show>
            </Show>

            <Show when={activeTab() === "users"}>
              <div class="wg-actions">
                <button type="button" onClick={() => void refreshUsers()} disabled={busy() || !connected()}>
                  Rafraîchir utilisateurs authentifiés
                </button>
                <span class="wg-muted">{authUsers().length} sessions détectées par parsing CLI/logs</span>
              </div>
              <div class="wg-table-wrap">
                <table class="wg-table">
                  <thead>
                    <tr>
                      <th>Utilisateur</th>
                      <th>IP</th>
                      <th>Type</th>
                      <th>Groupes</th>
                      <th>Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    <For each={authUsers()}>
                      {(u) => (
                        <tr>
                          <td>{u.user}</td>
                          <td>{u.ip ?? "n/a"}</td>
                          <td>{u.authType ?? "n/a"}</td>
                          <td>{joinShort(u.groups, "n/a")}</td>
                          <td>{u.source ?? ""}</td>
                        </tr>
                      )}
                    </For>
                  </tbody>
                </table>
              </div>
              <details class="wg-details">
                <summary>Sorties CLI brutes</summary>
                <pre class="wg-output">
                  {users()?.authLog || users()?.vpnStatus || users()?.users || "Aucune donnée chargée."}
                </pre>
              </details>
            </Show>

            <Show when={activeTab() === "config"}>
              <div class="wg-actions">
                <button type="button" onClick={() => void refreshOverview()} disabled={busy() || !connected()}>
                  Overview
                </button>
                <button type="button" onClick={() => void refreshConfig()} disabled={busy() || !connected()}>
                  Export config
                </button>
                <button type="button" onClick={() => void refreshConfigTree()} disabled={busy()}>
                  Arbre cache
                </button>
                <span class="wg-muted">
                  {lastRefresh() ? `${lastRefresh()!.redactedBytes} octets indexés` : "lecture seule, secrets redacted"}
                </span>
              </div>
              <div class="wg-config-grid">
                <section>
                  <h3>Arbre configuration</h3>
                  <For each={configTree()?.sections ?? []}>
                    {(section) => (
                      <div class="wg-tree-section">
                        <strong>
                          {section.title} ({section.count})
                        </strong>
                        <For each={section.items.slice(0, 250)}>
                          {(item) => (
                            <button type="button" onClick={() => void openRule(item.label)}>
                              <span>{item.label}</span>
                              <small>{item.summary ?? ""}</small>
                            </button>
                          )}
                        </For>
                      </div>
                    )}
                  </For>
                </section>
                <section>
                  <h3>Overview Fireware</h3>
                  <For each={overview()?.sections ?? []}>
                    {(section) => (
                      <details class="wg-details" open={section.id === "link-monitor"}>
                        <summary>
                          {section.title} · {section.command}
                        </summary>
                        <pre class="wg-output">{section.output}</pre>
                      </details>
                    )}
                  </For>
                </section>
              </div>
            </Show>

            <Show when={activeTab() === "tcpdump"}>
              <div class="wg-actions">
                <select value={tcpdumpPreset()} onChange={(e) => setTcpdumpPreset(e.currentTarget.value)}>
                  <option value="dns">DNS</option>
                  <option value="tls">TLS 443</option>
                  <option value="vpn">VPN</option>
                  <option value="icmp">ICMP</option>
                  <option value="host">Host</option>
                  <option value="port">Port</option>
                </select>
                <input
                  placeholder="host cible"
                  value={tcpdumpTarget()}
                  onInput={(e) => setTcpdumpTarget(e.currentTarget.value)}
                />
                <input
                  placeholder="port"
                  value={tcpdumpPort()}
                  onInput={(e) => setTcpdumpPort(e.currentTarget.value)}
                />
                <Show
                  when={tcpdumpRunning()}
                  fallback={
                    <button
                      type="button"
                      class="wg-primary"
                      disabled={busy() || !connected()}
                      onClick={() => void startTcpdump()}
                    >
                      Lancer
                    </button>
                  }
                >
                  <button type="button" onClick={() => void stopTcpdump()}>
                    Stop
                  </button>
                </Show>
                <button type="button" onClick={() => void refreshTcpdumpStatus()}>
                  Statut
                </button>
              </div>
              <div class="wg-command">{tcpdumpCommand() ?? "tcpdump -n -q -c 80 -s 160 ..."}</div>
              <pre class="wg-output wg-output-tcpdump">
                {tcpdumpLines().join("\n") || "Aucune sortie tcpdump chargée."}
              </pre>
            </Show>

            <Show when={activeTab() === "cache"}>
              <div class="wg-actions">
                <button type="button" onClick={() => void refreshCache()}>
                  Rafraîchir stats SQLite
                </button>
              </div>
              <div class="wg-cache-grid">
                <div>
                  <span>Snapshots</span>
                  <strong>{cacheStats()?.snapshots ?? 0}</strong>
                </div>
                <div>
                  <span>Règles</span>
                  <strong>{cacheStats()?.rules ?? 0}</strong>
                </div>
                <div>
                  <span>Traffic</span>
                  <strong>
                    {cacheStats()?.traffic ?? 0}/{cacheStats()?.trafficCap ?? 0}
                  </strong>
                </div>
                <div>
                  <span>tcpdump</span>
                  <strong>
                    {cacheStats()?.tcpdump ?? 0}/{cacheStats()?.tcpdumpCap ?? 0}
                  </strong>
                </div>
              </div>
              <pre class="wg-output">{cacheStats()?.dbPath ?? "Cache non initialisé."}</pre>
            </Show>
          </section>

          <aside class="wg-drawer" aria-label="Détail policy">
            <header>
              <span class="wg-muted">Policy ouverte</span>
              <strong>
                {wgPolicyDetail()?.policy
                  ? `#${wgPolicyDetail()!.policy.order} ${wgPolicyDetail()!.policy.name}`
                  : (selectedRule()?.rule?.name ?? selectedRuleName() ?? "Aucune")}
              </strong>
            </header>
            <Show
              when={wgPolicyDetail()}
              fallback={
                <Show
                  when={selectedRule()}
                  fallback={<p class="wg-muted">Sélectionner une policy ou une règle du Traffic Monitor.</p>}
                >
                  {(legacy) => (
                    <div class="wg-rule-detail">
                      <Show when={legacy().rule}>
                        {(rule) => (
                          <>
                            <span class="wg-action" data-tone={actionTone(rule().action)}>
                              {rule().action ?? "n/a"}
                            </span>
                            <dl>
                              <dt>Source</dt>
                              <dd>{joinShort(rule().sources)}</dd>
                              <dt>Destination</dt>
                              <dd>{joinShort(rule().destinations)}</dd>
                              <dt>Service</dt>
                              <dd>{joinShort(rule().services)}</dd>
                              <dt>NAT</dt>
                              <dd>{rule().nat ?? "n/a"}</dd>
                              <dt>Proxy</dt>
                              <dd>{rule().proxy ?? "n/a"}</dd>
                            </dl>
                            <pre>{legacy().liveOutput ?? rule().raw}</pre>
                          </>
                        )}
                      </Show>
                    </div>
                  )}
                </Show>
              }
            >
              {(detail) => {
                const p = detail().policy;
                const g = pmActionGlyph(p.action);
                return (
                  <div class="wg-policy-detail">
                    <div class="wg-policy-detail-head" data-tone={g.tone}>
                      <span class="wg-pm-action-icon" data-tone={g.tone}>
                        {g.icon}
                      </span>
                      <span class="wg-pm-action-label">{g.label}</span>
                      <Show when={!p.enabled}>
                        <span class="wg-pill">désactivée</span>
                      </Show>
                      <Show when={p.leafName}>
                        <span class="wg-muted"> · feuille {p.leafName}</span>
                      </Show>
                    </div>
                    <Show when={p.description}>
                      <p class="wg-muted">{p.description}</p>
                    </Show>

                    <h4>Members</h4>
                    <div class="wg-members-grid">
                      <div>
                        <strong>From ({p.fromResolved.length})</strong>
                        <ResolveTreeView nodes={detail().fromTree} />
                      </div>
                      <div>
                        <strong>To ({p.toResolved.length})</strong>
                        <ResolveTreeView nodes={detail().toTree} />
                      </div>
                    </div>

                    <h4>Service</h4>
                    <dl>
                      <dt>Nom</dt>
                      <dd>{p.service ?? "—"}</dd>
                      <Show when={detail().serviceEntry}>
                        {(se) => (
                          <>
                            <dt>Protocole / port</dt>
                            <dd>
                              {se()
                                .items.map((i) => `${i.protocol}${i.port ? `/${i.port}` : ""}`)
                                .join(", ") || "—"}
                            </dd>
                          </>
                        )}
                      </Show>
                    </dl>

                    <h4>Advanced</h4>
                    <dl>
                      <dt>Activée</dt>
                      <dd>{p.enabled ? "oui" : "non"}</dd>
                      <dt>Schedule</dt>
                      <dd>
                        {p.schedule ?? "—"}
                        <Show when={detail().scheduleEntry}> ({detail().scheduleEntry!.summary})</Show>
                      </dd>
                      <dt>Log</dt>
                      <dd>
                        {p.log ? "oui" : "non"}
                        <Show when={p.logForReport}> (pour rapport)</Show>
                      </dd>
                      <dt>IPS</dt>
                      <dd>{p.ips ? "oui" : "non"}</dd>
                      <dt>Geolocation</dt>
                      <dd>{p.geoAction ?? "Global"}</dd>
                      <Show when={p.appAction}>
                        <dt>App Control</dt>
                        <dd>{p.appAction}</dd>
                      </Show>
                      <Show when={p.sdwanAction}>
                        <dt>SD-WAN</dt>
                        <dd>{p.sdwanAction}</dd>
                      </Show>
                      <Show when={p.tlsSetting}>
                        <dt>TLS</dt>
                        <dd>{p.tlsSetting}</dd>
                      </Show>
                      <Show when={p.tags.length > 0}>
                        <dt>Tags</dt>
                        <dd>
                          <For each={p.tags}>{(tag) => <span class="wg-pill wg-tag">{tag}</span>}</For>
                        </dd>
                      </Show>
                      <Show when={p.connectionRate != null}>
                        <dt>Connection rate</dt>
                        <dd>{p.connectionRate}</dd>
                      </Show>
                      <Show when={p.qosPriority}>
                        <dt>QoS</dt>
                        <dd>{p.qosPriority}</dd>
                      </Show>
                      <Show when={p.policyRouting}>
                        <dt>Policy routing</dt>
                        <dd>{p.policyRouting}</dd>
                      </Show>
                      <Show when={p.autoBlock != null}>
                        <dt>Auto-block</dt>
                        <dd>{p.autoBlock ? "oui" : "non"}</dd>
                      </Show>
                      <Show when={p.notification != null}>
                        <dt>Notification</dt>
                        <dd>{p.notification ? "oui" : "non"}</dd>
                      </Show>
                      <Show when={p.idleTimeout != null}>
                        <dt>Idle timeout</dt>
                        <dd>{p.idleTimeout}</dd>
                      </Show>
                    </dl>

                    <h4>NAT</h4>
                    <Show when={p.nat || p.applyOneToOneNat || p.applyDnatGlobal || detail().natRule}>
                      <dl>
                        <dt>Rule</dt>
                        <dd>{p.nat ?? "—"}</dd>
                        <Show when={p.applyOneToOneNat}>
                          <dt>1:1 global</dt>
                          <dd>appliqué</dd>
                        </Show>
                        <Show when={p.applyDnatGlobal}>
                          <dt>DNAT global</dt>
                          <dd>appliqué</dd>
                        </Show>
                        <Show when={detail().natRule}>
                          {(nr) => (
                            <For each={nr().items}>
                              {(it) => (
                                <>
                                  <dt>_ext→cible</dt>
                                  <dd class="wg-muted">
                                    {it.extAddrName ?? "?"} @ {it.interface ?? "?"} → {it.addrName ?? "?"}{" "}
                                    {it.addrResolved.length ? `(${it.addrResolved.join(", ")})` : ""}
                                    {it.port ? `:${it.port}` : ""}
                                  </dd>
                                </>
                              )}
                            </For>
                          )}
                        </Show>
                      </dl>
                    </Show>

                    <Show when={p.proxy || detail().proxyAction}>
                      <h4>Proxy</h4>
                      <dl>
                        <dt>Action</dt>
                        <dd>{p.proxy ?? "—"}</dd>
                        <Show when={detail().proxyAction}>
                          {(pa) => (
                            <>
                              <dt>Type</dt>
                              <dd>
                                {pa().type}
                                {pa().typeAttr ? ` (${pa().typeAttr})` : ""}
                              </dd>
                              <dt>IPS</dt>
                              <dd>
                                {pa().ipsEnabled ? "oui" : "non"}
                                <Show when={pa().ipsSpyware}> (spyware)</Show>
                                <Show when={pa().ipsProtectionType}> ({pa().ipsProtectionType})</Show>
                              </dd>
                              <dt>WebBlocker</dt>
                              <dd>{pa().webBlockerEnabled ? "oui" : "non"}</dd>
                              <dt>Trusted hosts</dt>
                              <dd>{pa().trustedHostsCount}</dd>
                              <Show when={pa().requestTimeout != null}>
                                <dt>Request timeout</dt>
                                <dd>{pa().requestTimeout}s</dd>
                              </Show>
                            </>
                          )}
                        </Show>
                      </dl>
                    </Show>

                    <Show when={detail().proxyAction?.reverseProxy}>
                      {(rp) => (
                        <>
                          <h4>Reverse proxy HTTPS</h4>
                          <dl>
                            <Show when={rp().redirectTo}>
                              <dt>Redirection</dt>
                              <dd>{rp().redirectTo}</dd>
                            </Show>
                            <Show when={rp().tlsProfile}>
                              <dt>Profil TLS</dt>
                              <dd>{rp().tlsProfile}</dd>
                            </Show>
                            <Show when={rp().tlsMinClient || rp().tlsMinServer}>
                              <dt>TLS min</dt>
                              <dd>
                                client {rp().tlsMinClient ?? "?"} / serveur {rp().tlsMinServer ?? "?"}
                              </dd>
                            </Show>
                            <Show when={rp().timeout != null}>
                              <dt>Timeout</dt>
                              <dd>{rp().timeout}s</dd>
                            </Show>
                          </dl>
                          <Show when={rp().domainRules.length > 0}>
                            <div class="wg-table-wrap">
                              <table class="wg-table wg-rp-table">
                                <thead>
                                  <tr>
                                    <th>Host public</th>
                                    <th>Backend</th>
                                    <th>Proxy HTTP inspecteur</th>
                                    <th>Cert</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <For each={rp().domainRules}>
                                    {(rule) => (
                                      <tr classList={{ "wg-rp-fallthrough": rule.fallthrough }}>
                                        <td>
                                          <Show
                                            when={!rule.fallthrough}
                                            fallback={<span class="wg-muted">{rule.name}</span>}
                                          >
                                            <strong>{rule.name}</strong>
                                            <Show when={rule.pattern}>
                                              <small class="wg-muted">
                                                {" "}
                                                {rule.matchType === "regexp" ? "regex " : ""}
                                                {rule.pattern}
                                              </small>
                                            </Show>
                                          </Show>
                                        </td>
                                        <td>
                                          <Show when={rule.ipv4Dest} fallback={<span class="wg-muted">—</span>}>
                                            <span class="wg-rp-backend">{rule.ipv4Dest}</span>
                                          </Show>
                                        </td>
                                        <td>{rule.inspect ?? <span class="wg-muted">—</span>}</td>
                                        <td>{rule.serverCert ?? <span class="wg-muted">—</span>}</td>
                                      </tr>
                                    )}
                                  </For>
                                </tbody>
                              </table>
                            </div>
                          </Show>
                        </>
                      )}
                    </Show>

                    <Show when={detail().relatedTraffic.length > 0}>
                      <h4>Traffic lié</h4>
                      <For each={detail().relatedTraffic.slice(0, 12)}>
                        {(row) => (
                          <button type="button" class="wg-traffic-chip" onClick={() => setActiveTab("traffic")}>
                            {fmtTime(row.time)} {row.src} → {row.dst}
                          </button>
                        )}
                      </For>
                    </Show>

                    <details class="wg-details">
                      <summary>Config brute (XML &lt;abs-policy&gt;)</summary>
                      <pre class="wg-output">{p.rawXml}</pre>
                    </details>
                    <Show when={detail().liveOutput}>
                      <details class="wg-details">
                        <summary>Sortie live (show rule)</summary>
                        <pre class="wg-output">{detail().liveOutput}</pre>
                      </details>
                    </Show>
                  </div>
                );
              }}
            </Show>
          </aside>
        </main>
      </div>
    </PanelLayout>
  );
};

const WatchGuardRulesPanel: Component = () => (
  <WatchGuardPanel initialTab="rules" lockedTab title="WatchGuard · Règles" />
);
const WatchGuardTrafficPanel: Component = () => (
  <WatchGuardPanel initialTab="traffic" lockedTab title="WatchGuard · Traffic Monitor" />
);
const WatchGuardLinksPanel: Component = () => (
  <WatchGuardPanel initialTab="links" lockedTab title="WatchGuard · Link monitors" />
);
const WatchGuardUsersPanel: Component = () => (
  <WatchGuardPanel initialTab="users" lockedTab title="WatchGuard · Utilisateurs authentifiés" />
);
const WatchGuardConfigPanel: Component = () => (
  <WatchGuardPanel initialTab="config" lockedTab title="WatchGuard · Configuration" />
);
const WatchGuardTcpdumpPanel: Component = () => (
  <WatchGuardPanel initialTab="tcpdump" lockedTab title="WatchGuard · tcpdump" />
);

registerPanel("watchguard", WatchGuardPanel);
registerPanel("watchguard-rules", WatchGuardRulesPanel);
registerPanel("watchguard-traffic", WatchGuardTrafficPanel);
registerPanel("watchguard-links", WatchGuardLinksPanel);
registerPanel("watchguard-users", WatchGuardUsersPanel);
registerPanel("watchguard-config", WatchGuardConfigPanel);
registerPanel("watchguard-tcpdump", WatchGuardTcpdumpPanel);
